// Cœur de simulation : un tick fait avancer le monde d'une sous-unité au plus.
// Version Task 4 : coureurs seulement (marche + chute + fausses briques).
// Les crochets des tâches suivantes (bombes, trous, moines, or, glace...) sont
// des fonctions vides, remplies au fil des tâches sans changer ce contrat.
import { canDig, digAt, digSand, settleLoose } from './dig'
import { dropCellFor, killMonk, stepMonks, wakeStatues } from './monks'
import { hasSupport, hasSupportCenter, isLadder, blocksFrom, isSolid, standsOnIce, tileAt } from './physics'
import { rngInt, tieBreakWinner } from './rng'
import { cellAt, centerOf, crateAt, Crate, GameEvent, Fish, GameState, Hole, Monk, orRestant, Runner, newMonk } from './state'
import { tryTeleport } from './teleport'
import { BREATH_REFILL, BREATH_TICKS, CAPTIF, FUSE_TICKS, ItemKind, MEDUSE_STUN_TICKS, MONK_RESPAWN_TICKS, NO_INPUT, PlayerInput, FILL_COUPS, FILL_TICKS, REGROW_TICKS, REVIVE_INVULN_TICKS, ROPE_CHARGES, RUNNER_RESPAWN_TICKS, STUN_TICKS_MONK, STUN_TICKS_PLAYER, SUB, T, TAZER_CHARGES, TAZER_RANGE, cellIndex, colOf, rowOf } from './types'

// Ordre d'un tick (contrat verrouillé pour toutes les tâches suivantes) :
// 1. s.events = [] ; s.tick++
// 2. bombes (Task 11) : décompte, explosion
// 3. trous (Task 6) : repousse + ensevelissement
// 4. coureurs : creusage puis mouvement (1 sous-unité max) puis ramassages/téléport (Task 7, 12).
//    Ordre de visite ALTERNÉ selon la parité du tick (Tâche 3.5, anti-biais J1) — cf. la boucle.
// 5. moines (Task 8-9) : IA + mouvement (sauf tick % 5 === 4) + or
// 6. collisions moine/coureur -> morts (Task 8)
// 6bis. respawns multi (lot 4b Task 2) : les morts de versus/coop réapparaissent —
//       APRÈS les collisions (grâce d'un tick au spawn), AVANT la victoire
// 7. révélation de sortie + victoire (Task 7)
export function tick(s: GameState, inputs: PlayerInput[]): GameEvent[] {
  s.events = []
  s.tick++
  if (s.finished) return s.events
  stepBombs(s) // Task 11
  stepCrumble(s) // tuiles P1 T3 : minuteurs de sol friable -> casse AVANT stepHoles (visible au snapshot)
  stepHoles(s) // Task 6
  // ORDRE DE VISITE TOURNANT (Tâche 3.5) : la boucle d'ACTION traitait les coureurs dans l'ordre
  // FIXE 0,1,2… -> J1 (index 0) toujours servi AVANT les autres sur les actions SIMULTANÉES exactes
  // (ramassage d'or / poussée de caisse au même tick) = micro-biais J1. On fait TOURNER l'ordre par
  // ROTATION CYCLIQUE selon le tick : `i = (k + s.tick) % n`. C'est une BIJECTION sur [0, n-1] (chaque
  // coureur visité une fois), et le PREMIER servi (k=0) = `s.tick % n` avance d'un cran à chaque tick :
  // sur n ticks consécutifs, CHAQUE joueur — index du milieu compris — est premier exactement une fois
  // (équité stricte à n joueurs). Pour n=2 la rotation redonne exactement l'alternance pair/impair
  // précédente (aucun changement en 2 joueurs) ; pour n=3 elle corrige le biais où l'index 1 n'était
  // JAMAIS premier avec une simple parité. Fonction PURE de s.tick (ZÉRO rng : ne consomme aucun tirage,
  // la séquence des spawns/moines/événements reste intacte), donc serveur et clients calculent le MÊME
  // ordre (tick partagé) et un rejeu est identique. `i` reste l'index RÉEL du coureur (identité +
  // inputs[i] stables) et le CORPS du tick est INCHANGÉ : seul l'ordre de visite change. On ne fait
  // tourner QUE cette boucle d'action ; les passes order-independent (moines, collisions, respawns,
  // victoire — cette dernière déjà départagée par seed en 3.4) ne sont pas touchées.
  const n = s.runners.length
  for (let k = 0; k < n; k++) {
    const i = (k + s.tick) % n
    const r = s.runners[i]!
    if (!r.alive) continue
    let inp = inputs[i] ?? NO_INPUT
    // MOMIFIÉ (05/08) : il ne JOUE plus, il SE DÉBAT — ses touches de pioche servent à marteler
    // en alternance, tout le reste est ignoré. La physique continue (stepRunner tourne quand
    // même) : un cocon posé sur un sol qu'on lui creuse tombe, comme n'importe qui.
    // ⚠️ L'ÉCHÉANCE se teste À PART du « est-il momifié » : avec la convention `> tick`, le tick
    // de libération ne rentre plus dans la branche « momifié » — sans ce test, le cocon
    // s'éteignait en silence, sans son ni événement.
    if (r.momieUntil > 0) {
      if (s.tick >= r.momieUntil) demomifie(s, r)
      else { debatMomie(s, r, inp); inp = NO_INPUT }
    }
    // Coureur étourdi (tazer) : ses inputs sont ignorés (NO_INPUT), mais stepRunner
    // s'exécute quand même -> la physique/chute continue (figé mais vulnérable).
    if (r.stunUntil >= s.tick) inp = NO_INPUT
    stepRunner(s, r, inp)
  }
  settleLoose(s) // 11/07 : gravité de l'or/objets (chute dans les trous frais, y compris bombes)
  stepCrates(s) // Phase 4 (#2) : gravité des caisses + casse (bois >=2 cases) + écrasement — APRÈS le mouvement des coureurs (poussée) et l'or, AVANT les moines (qui voient la position finale)
  stepMonks(s) // Task 8
  stepFishes(s) // ennemis aquatiques (16/07 soir) : patrouille/charge/oscillation + leurs contacts
  stepDrapeaux(s) // DRAPEAU-CRAPAUD (05/08) : vol du fanion + capture (transformation en crapaud)
  stepGeants(s) // GÉANT (05/08) : bousculade des adversaires chevauchés (2 cases de vol plané)
  stepDeguises(s) // DÉGUISEMENT (05/08) : le contact du faux moine tue les adversaires (versus)
  stepBandelettes(s) // MOMIE (05/08, Q1A) : un adversaire qui traverse un piège est enroulé
  stepPatates(s) // PATATE CHAUDE (05/08) : allumage, transfert au contact, explosion à l'échéance
  stepMeute(s)   // MEUTE PRIVÉE (05/08) : évaporation des spectres (échéance, cible morte, trou)
  stepCouronnes(s) // COURONNE MAUDITE (05/08) : ramassage au contact + une pièce par seconde
  stepButtons(s) // circuit interrupteurs/barrières (17/07), sur les positions finales du tick
  checkCollisions(s) // Task 8
  stepEvents(s) // mode survie : événements aléatoires (versus + eventsEnabled ; seedés)
  // lot 4b Task 2 : APRÈS checkCollisions, la position fait la grâce — les collisions
  // du tick sont passées, le ressuscité ne peut pas mourir le tick même.
  stepRespawns(s)
  checkWin(s) // Task 7
  return s.events
}

// Mort d'un coureur. Solo : le contrôleur gère la suite (comportement historique).
// Versus/coop : réapparition programmée à +90 ticks ; en versus, une pièce portée est
// reposée sur la case praticable stable la plus proche du lieu de mort (dropCellFor).
function killRunner(s: GameState, r: Runner, cause: 'monk' | 'buried' | 'bomb' | 'fell' | 'lava' | 'deathzone' | 'crushed' | 'drowned' | 'fire' | 'spikes' | 'fish') {
  r.alive = false
  s.events.push({ type: 'death', runner: s.runners.indexOf(r), cause })
  // LE MAILLET NE MEURT PAS AVEC SON PORTEUR (05/08) : il retombe sur la case stable la plus
  // proche, prêt pour le suivant, et la pioche plantée DISPARAÎT — le respawn rend un creuseur
  // complet (personne ne retraverse la carte pour une pioche orpheline).
  if (r.maillet) {
    r.maillet = false
    const idx = s.runners.indexOf(r)
    const to = dropCellFor(s, cellAt(r.x, r.y, s.cols))
    s.maillets.push(to)
    s.maillets.sort((a, b) => a - b)
    s.pioches = s.pioches.filter((p) => p.owner !== idx)
    s.events.push({ type: 'malletRendu', runner: idx, cell: to })
  }
  // LA COURONNE ROULE (05/08) : elle ne quitte la tête qu'à la mort — elle tombe sur la case
  // stable la plus proche et attend le prochain ambitieux. La malédiction ne meurt pas avec toi.
  if (r.couronne) {
    r.couronne = false
    const to = dropCellFor(s, cellAt(r.x, r.y, s.cols))
    s.couronnes.push(to)
    s.couronnes.sort((a, b) => a - b)
    s.events.push({ type: 'couronneTombee', cell: to })
  }
  if (s.mode === 'solo') {
    // CHECKPOINT (16/07 soir) : un ver activé réanime AUSSI en solo — la partie CONTINUE
    // (or ramassé conservé, trous, moines) au lieu du restart complet. Le client solo
    // décompte la vie et fige la partie lui-même quand elles sont épuisées.
    if (r.checkpoint >= 0) r.respawnAt = s.tick + RUNNER_RESPAWN_TICKS
    return
  }
  // Versus : une pièce portée retombe sur la case stable la plus proche (inchangé).
  if (s.mode === 'versus' && r.gold > 0) {
    r.gold--
    const to = dropCellFor(s, cellAt(r.x, r.y, s.cols))
    s.golds.push(to)
    s.golds.sort((a, b) => a - b)
    s.events.push({ type: 'goldDropped', cell: to })
  }
  // Mode survie (versus fini, s.lives>0) : chaque mort coûte une vie ; à 0 = ÉLIMINÉ, on NE
  // programme PAS de réapparition (respawnAt reste 0, stepRespawns ne le ressuscite jamais ;
  // checkWin donnera la victoire à l'adversaire). Illimité (s.lives===0) ou coop : réapparition.
  if (s.mode === 'versus' && s.lives > 0) {
    r.lives = Math.max(0, r.lives - 1)
    if (r.lives === 0) return
  }
  r.respawnAt = s.tick + RUNNER_RESPAWN_TICKS
}

// Réapparition des coureurs (lot 4b Task 2). Versus/coop : toujours ; SOLO : uniquement les
// morts avec un CHECKPOINT actif (killRunner ne programme respawnAt qu'à cette condition).
// À respawnAt : le coureur revit au CENTRE du checkpoint s'il en a un, sinon de sa case de
// départ, drapeaux remis à neuf, mains vides.
function stepRespawns(s: GameState) {
  let revived = false
  for (const r of s.runners) {
    if (r.alive || r.respawnAt === 0 || s.tick < r.respawnAt) continue
    const { x, y } = centerOf(r.checkpoint >= 0 ? r.checkpoint : r.spawnCell, s.cols)
    r.x = x; r.y = y
    r.alive = true; r.respawnAt = 0; revived = true
    r.falling = false; r.onLadder = false; r.onRope = false
    r.digHeld = false; r.teleLockCell = -1; r.item = null; r.face = 1
    r.stunUntil = 0 // sinon un coureur tazé puis tué (respawn < STUN_TICKS_PLAYER) réapparaîtrait gelé
    r.bounce = 0 // sinon un coureur mort EN PLEIN REBOND (moine/lave sur un trampoline) réapparaîtrait en montee
    r.bounceDir = 0 // idem : pas de derive de rebond residuelle a la reapparition (Lot C)
    r.rampHop = 0 // AUDIT 16/07 (critique) : mort EN PLEIN HOP de rampe -> le ressuscite repartait en
    // diagonale VERROUILLEE depuis son spawn (moveRunner traite rampHop AVANT tout), traversant les murs
    // jusqu'a re-mourir = cascade de morts. Miroir exact de `m.hop = 0` cote moine (monks.ts, killMonk).
    r.breath = BREATH_TICKS // idem : noye puis ressuscite = souffle PLEIN (sinon il renait a 0 et se
    // re-noie aussitot — mortel sur un ver checkpoint AQUATIQUE : boucle de morts a la reanimation)
    r.invulnUntil = s.tick + REVIVE_INVULN_TICKS // bouclier ANTI-MOINE bref (16/07 soir) : un moine
    // campant le ver checkpoint (ou le spawn multi) ne transforme plus la resurrection en boucle de
    // morts. Contact moine SEUL : lave/feu/pics tuent toujours (on n'y marche pas tout seul).
    r.crapaud = false // CRAPAUD (05/08) : mourir en crapaud = renaître HUMAIN (la forme ne survit pas)
    r.hop = 0 // ... et jamais de bond résiduel au spawn (miroir exact de rampHop ci-dessus)
    r.momieUntil = 0; r.momieCoups = 0; r.momieCote = 0 // ni cocon résiduel : on renaît libre de ses mouvements
  }
  if (revived) repopItems(s) // à la résurrection, le niveau retrouve ses objets ramassables (anti soft-lock)
}

// REPOP DES OBJETS À LA MORT (18/07, demande Claude-Alain « quand on meurt les objets doivent
// repop »). Le respawn rend le coureur les mains vides (r.item = null) et rien ne remettait dans
// le niveau les objets ramassés puis perdus/consommés : ramasser l'unique marteau puis mourir
// laissait le passage à jamais infranchissable (soft-lock). Ici, à chaque résurrection, on
// RÉAPPROVISIONNE le niveau jusqu'à son stock de DÉPART (s.initialItems), par type, en ne comptant
// QUE ce qui MANQUE — un objet encore au sol ou porté par un coureur vivant n'est jamais dupliqué.
// La clé est EXCLUE : elle vit dans un compteur (r.keys) conservé au respawn, donc jamais perdue.
// L'or et les objets lâchés par les caisses cassées ne sont pas concernés (progrès conservé).
function repopItems(s: GameState): void {
  const initialByKind = new Map<ItemKind, number[]>() // type -> cellules de départ
  for (const it of s.initialItems) {
    if (it.kind === 'key') continue
    const cells = initialByKind.get(it.kind) ?? []
    cells.push(it.cell); initialByKind.set(it.kind, cells)
  }
  for (const [kind, cells] of initialByKind) {
    const onGround = s.items.reduce((n, i) => n + (i.kind === kind ? 1 : 0), 0)
    const carried = s.runners.reduce((n, r) => n + (r.alive && r.item === kind ? 1 : 0), 0)
    let missing = cells.length - onGround - carried
    if (missing <= 0) continue
    for (const cell of cells) {
      if (missing <= 0) break
      if (s.items.some((i) => i.cell === cell)) continue // cellule de départ déjà occupée : on saute
      s.items.push({ cell, kind })
      missing--
    }
  }
}

function stepRunner(s: GameState, r: Runner, inp: PlayerInput) {
  // GÉANT (05/08) : fin des 6 s pile ce tick -> il rétrécit sur place avec un « pof » (aucun
  // autre malus). geantUntil garde sa valeur périmée, comme dashUntil (les tests sont des >).
  if (r.alive && r.geantUntil === s.tick) s.events.push({ type: 'degeant', runner: s.runners.indexOf(r) })
  // DÉGUISEMENT (05/08) : fin des 10 s -> pouf de fumée qui révèle, et une demi-seconde
  // d'immunité BILATÉRALE : lui ne tue plus (deguiseUntil est passé), et personne ne le tue
  // « à la frontière » de l'effet (invulnUntil couvre moines, faux moines et bousculades).
  // FANTÔME (05/08) : fin des 4 s -> réapparition en fondu d'une demi-seconde (le client mène
  // le fondu ; ici on ne fait que dire l'instant). Aucun pouf : on ne punit pas la position de
  // celui qui n'a rien demandé.
  if (r.alive && r.fantomeUntil === s.tick) s.events.push({ type: 'defantome', runner: s.runners.indexOf(r) })
  if (r.alive && r.deguiseUntil === s.tick) {
    r.invulnUntil = Math.max(r.invulnUntil, s.tick + DEGUISE_GRACE)
    s.events.push({ type: 'dedeguise', runner: s.runners.indexOf(r) })
  }
  // CRAPAUD (05/08) : il n'a pas de pioche — la touche creuser DÉCRAPOTISE (un geste par
  // pression, verrou digHeld partagé avec tryDig). Le relâchement passe par la branche normale :
  // tryDig remet digHeld à false quand aucune touche creuser n'est pressée.
  if (r.crapaud && (inp.digL || inp.digR)) {
    // Jamais en plein bond (r.hop > 0) : redevenir humain en l'air prolongerait la glissade
    // verrouillée sans forme de crapaud. La touche TENUE se déclenche à l'atterrissage.
    if (!r.digHeld && r.hop === 0) {
      r.digHeld = true
      r.crapaud = false
      s.events.push({ type: 'decrapaud', runner: s.runners.indexOf(r) })
    }
  }
  // LE MAILLET FRAPPE (05/08, l'idée de Claude-Alain : « remplace la pioche ») : les touches
  // creuser deviennent des coups gauche/droite, à l'infini, cadence une demi-seconde. La touche
  // TENUE re-frappe à la cadence (un marteau se balance, il ne se « presse » pas). Pendant une
  // forme (crapaud/géant/déguisé), le maillet est RANGÉ dans le dos : ni frappe ni creusage.
  else if (r.maillet && (inp.digL || inp.digR) && r.geantUntil <= s.tick && r.deguiseUntil <= s.tick) {
    if (s.tick >= r.mailletReadyAt && r.y % SUB === 0 && !r.falling) {
      r.mailletReadyAt = s.tick + MAILLET_CD
      frappeMaillet(s, r, inp.digL ? -1 : 1)
    }
  }
  // REBOUCHAGE (31/07) d'abord : si la touche creuser vise un TROU, ce tick comble au lieu de
  // creuser. Sans cette priorité, tryDig émettrait un « refus » cosmétique à chaque pression sur
  // un trou (rien de creusable là), et le geste n'aurait aucun effet.
  else if (!tryRebouchage(s, r, inp)) tryDig(s, r, inp) // Task 6 : creusage AVANT le mouvement (contrat verrouillé)
  tryUse(s, r, inp) // Task 11 : pose de bombe / marteau-piqueur, APRÈS le creusage, AVANT le mouvement
  // PICS (16/07) : « était en chute CE tick » capturé AVANT le mouvement — à l'atterrissage,
  // moveRunner remet falling=false dans le MÊME tick, or atterrir SUR des pics doit tuer.
  // Variable locale au tick (rien dans l'état -> rien à sérialiser/hasher, déterminisme intact).
  const wasFalling = r.falling
  // PLOUF D'ENTRÉE (06/08) : la tuile occupée AVANT le mouvement, pour repérer l'instant où l'on
  // perce la surface. Variable locale au tick, comme `wasFalling` — rien dans l'état, donc rien à
  // sérialiser ni à hasher, et le plouf ne peut pas se rejouer en boucle en nageant sur place.
  const tuileAvant = tileAt(s, r.x >> 3, r.y >> 3)
  // PICS, suite (audit 16/07, décision D2) : altitude AVANT le mouvement. Les pics blessent toute
  // DESCENTE (chute, échelle, tapis descendant, rampe, nage vers le bas) — pas la marche latérale.
  // `y a augmenté ce tick` capture la descente quel qu'en soit le moteur, sans un test par branche.
  const prevY = r.y
  const xAvant = r.x // PAS DU PORTEUR DE MAILLET (06/08) : « a-t-il vraiment avancé ce tick ? »
  moveRunner(s, r, inp)
  // GÉANT, Q3A (choix Claude-Alain) : il DÉFONCE la brique au-dessus de sa tête — elle repousse
  // comme un trou creusé (digAt : mêmes éjections, même repousse, mêmes emmurés libérés). Testé
  // aligné sur sa case : chaque pas de couloir bas casse le plafond, et grandir sous une brique
  // la casse aussi (le dégagement de tête vit dans la même règle).
  if (r.alive && r.geantUntil > s.tick && r.x % SUB === 0 && r.y % SUB === 0 && (r.y >> 3) > 0) {
    if (tileAt(s, r.x >> 3, (r.y >> 3) - 1) === T.Brick) digAt(s, cellIndex(r.x >> 3, (r.y >> 3) - 1, s.cols))
  }
  // LE PAS DU PORTEUR DE MAILLET (06/08, développement du contrepoids). Un « clonk » sourd et un
  // nuage de poussière à chaque case franchie : le poids se marche, il ne se regarde pas
  // seulement. Événement COSMÉTIQUE (hors hash, zéro RNG, aucun effet sur l'état simulé —
  // même contrat que `denied` et `digAnim`), et parfaitement déterministe : il ne dépend que de
  // la position, donc les trois écrans d'un multi l'entendent au même instant.
  if (r.alive && r.maillet && !r.falling && r.hop === 0 && r.y % SUB === 0 && r.x % SUB === 0 && r.x !== xAvant) {
    s.events.push({ type: 'malletPas', runner: s.runners.indexOf(r) })
  }
  if (r.alive && r.rampHop === 0 && r.hop === 0) conveyorPush(s, r) // tuiles P2 T2 : derive du tapis, APRES le mouvement propre ; JAMAIS pendant un hop de rampe OU un bond de crapaud (desalignerait l'arrivee)
  collectAt(s, r) // Task 7
  // Bombes rouges (Lot C) : tant que le coureur est SUR une case BombRefill, son slot est regarni
  // en bombe chaque tick -> bombes a volonte tant qu'on y reste. Ne clobbe pas un tazer/marteau
  // porte (garde item null/bomb). Deterministe ; aucune case sans BombRefill n'est affectee.
  if (r.alive && tileAt(s, r.x >> 3, r.y >> 3) === T.BombRefill && (r.item === null || r.item === 'bomb')) r.item = 'bomb'
  // CRISTAL DE DASH (17/07) : une STATION, pas un consommable — le traverser pendant le
  // cooldown remet le dash à zéro (une fois par recharge : dash prêt = case inerte).
  if (r.alive && s.dashEnabled && r.dashReadyAt > s.tick && r.dashUntil <= s.tick && tileAt(s, r.x >> 3, r.y >> 3) === T.DashCrystal) {
    r.dashReadyAt = s.tick
    s.events.push({ type: 'dashCrystal', runner: s.runners.indexOf(r) })
  }
  // MARCHER SUR UN RESSORT SUFFIT (31/07, demande de Claude-Alain). Le rebond n'était amorcé que
  // par l'ATTERRISSAGE : en marchant on n'est pas en chute, donc le trampoline se comportait comme
  // une dalle ordinaire. Testé ICI, après le mouvement, pour que la colonne lue soit celle où le
  // coureur vient d'arriver — pas celle qu'il quittait.
  // Les gardes disent toutes la même chose : « il est POSÉ dessus, et rien d'autre ne le pilote ».
  if (r.alive && !r.falling && r.bounce === 0 && r.rampHop === 0 && r.hop === 0 && r.y % SUB === 0 && !r.onLadder && !r.onRope) {
    const ici = tileAt(s, r.x >> 3, r.y >> 3)
    // L'eau, le sable et les tapis verticaux surchargent la gravité et gèrent eux-mêmes la
    // position : un ressort sous l'un d'eux ne doit pas s'inviter dans leur mouvement.
    if (ici !== T.Water && ici !== T.Bubbles && ici !== T.Sand && ici !== T.ConveyorUp && ici !== T.ConveyorDown) {
      amorcerRebond(s, r)
    }
  }
  // CRAPAUD : un téléporteur au MILIEU d'un bond doit ÉTEINDRE le bond (leçon du sauteur,
  // audit 16/07) — sinon le crapaud repart en glissade verrouillée depuis l'arrivée, à travers
  // les murs. tryTeleport ne renvoie rien : un déplacement se lit sur la case avant/après.
  const cellAvantTp = cellAt(r.x, r.y, s.cols)
  tryTeleport(s, r) // Task 12 : téléportation au centre d'une case Teleporter, APRÈS le mouvement
  if (r.hop > 0 && cellAt(r.x, r.y, s.cols) !== cellAvantTp) r.hop = 0
  checkBottomFall(s, r) // 12/07 : chute hors de l'écran par le bas = perte de vie
  checkLava(s, r, wasFalling, prevY) // tuiles P1 + feu/pics : contact mortel, n'importe où (après le mouvement)
  // Souffle (EAU 16/07) : décompte immergé, recharge PLEINE dès un tick à l'air libre,
  // noyade à 0 (cause 'drowned'). Champ hashé/sérialisé automatiquement (spread générique).
  if (r.alive) {
    const ht = tileAt(s, r.x >> 3, r.y >> 3)
    // Le plouf : on ENTRE dans l'eau ce tick-ci (la case d'avant n'en était pas). Cosmétique.
    if (isWaterT(ht) && !isWaterT(tuileAvant)) s.events.push({ type: 'waterEnter', runner: s.runners.indexOf(r) })
    if (ht === T.Bubbles) {
      // SOURCE DE BULLES (16/07 soir) : recharge rapide, plafonnée au plein — la station au fond.
      r.breath = Math.min(BREATH_TICKS, r.breath + BREATH_REFILL)
    } else if (ht === T.Water && faitLaPlanche(s, r, inp)) {
      // LA PLANCHE (06/08) : le visage est au-dessus de la surface, on respire — lentement.
      r.breath = Math.min(BREATH_TICKS, r.breath + PLANCHE_REFILL)
    } else if (ht === T.Water) {
      r.breath--
      // AUDIT 16/07 : le dash rend invulnérable — noyade comprise (le contrat « le dash traverse
      // tout » ne souffre pas d'exception cachée). Le souffle continue de se vider pendant : dasher
      // ne recharge rien, on meurt à la sortie du dash si on est toujours sous l'eau.
      if (r.breath <= 0) { r.breath = 0; if (r.dashUntil <= s.tick) killRunner(s, r, 'drowned') }
    } else if (r.breath !== BREATH_TICKS) r.breath = BREATH_TICKS
  }
  // CHECKPOINT ver de terre (16/07 soir) : case-centre sur le CORPS du ver (rangée de l'ancre,
  // ±2 colonnes) = activé — ancre la plus PROCHE d'abord (ordre fixe : déterministe si deux vers
  // se chevauchent). Event émis au CHANGEMENT seulement (son + réveil visuel du ver) ; le champ
  // Runner.checkpoint est sérialisé/hashé automatiquement (spread générique).
  if (r.alive) {
    const col = r.x >> 3, row = r.y >> 3
    for (const d of [0, -1, 1, -2, 2]) {
      if (tileAt(s, col + d, row) !== T.Checkpoint) continue
      const cell = cellIndex(col + d, row, s.cols)
      if (r.checkpoint !== cell) { r.checkpoint = cell; s.events.push({ type: 'checkpoint', runner: s.runners.indexOf(r), cell }) }
      break
    }
  }
  // Filet #116-2/3 (16/07) : falling=false + y DÉSALIGNÉ hors situation verticale légitime
  // (échelle/corde, tapis vertical, rebond, eau, sable) = état IMPOSSIBLE où plus rien ne bouge
  // (la marche exige y aligné, la chute exige falling) — les limbos « échelle bas » et « sable
  // via dérive ». On rend le coureur à la GRAVITÉ : la branche chute et son snap-up existant
  // (atterrissage recalé vers le haut) le reposent au tick suivant. Couvre toute la famille,
  // quel que soit le chemin qui a produit l'état.
  if (r.alive && !r.falling && r.bounce === 0 && r.y % SUB !== 0) {
    const col = r.x >> 3, row = r.y >> 3
    const here = tileAt(s, col, row)
    const legit = r.rampHop > 0 || isLadder(here) || isLadder(tileAt(s, col, row + 1)) || here === T.Rope ||
      here === T.ConveyorUp || here === T.ConveyorDown || here === T.Sand || here === T.Water || here === T.Bubbles
    if (!legit) r.falling = true
  }
}

// Contact avec la lave = mort (comme un puits, mais n'importe où). Appelé APRÈS le mouvement
// et APRÈS checkBottomFall : un coureur déjà mort (tombé par le bas ce tick) n'est pas re-tué.
// Pur : simple test de la case-centre, aucun aléa. Un niveau sans lave ne déclenche jamais rien.
function checkLava(s: GameState, r: Runner, wasFalling: boolean, prevY: number): void {
  if (!r.alive) return
  if (r.dashUntil > s.tick) return // dash (Lot D) : franchit lave/zone (invulnerable pendant le dash)
  const t = tileAt(s, r.x >> 3, r.y >> 3)
  if (t === T.Lava) killRunner(s, r, 'lava')
  else if (t === T.DeathZone) killRunner(s, r, 'deathzone') // #1 : champ mortel traversable, tue au contact partout
  else if (t === T.Fire) killRunner(s, r, 'fire') // FEU (16/07) : mortel au contact, comme la lave mais posable partout
  // PICS — règle FINALE (décision D2 de l'audit 16/07) : les pics blessent tout ce qui DESCEND dessus,
  // pas ce qui passe à côté. Descendre = « y a augmenté ce tick » (r.y > prevY), quel que soit le
  // moteur : chute, échelle, tapis descendant, hop de rampe, nage vers le bas, enfoncement dans le
  // sable. `falling || wasFalling` reste dans le test car à l'atterrissage moveRunner remet falling à
  // false dans le MÊME tick. La MARCHE latérale au travers (y inchangé) reste sans danger : c'est ce
  // qui distingue les pics de la lave, et c'est ce qui en fait un piège de VERTICALITÉ. Avant : on
  // pouvait descendre tranquillement une échelle plantée en plein dans les pics.
  else if (t === T.Spikes && (r.falling || wasFalling || r.y > prevY)) killRunner(s, r, 'spikes')
}

// Chute hors de l'écran par le BAS (demande Claude-Alain 12/07) : un coureur qui atteint la
// DERNIÈRE rangée est passé à travers un puits ouvert jusqu'au bord bas (les vraies cartes ont
// un sol plein en rangée 15 ; s'y retrouver = y être tombé). killRunner s'occupe de la suite
// (solo : mort, le contrôleur décompte la vie ; versus/coop : réapparition programmée). Exception :
// une échelle ou une corde en dernière rangée = présence INTENTIONNELLE, pas une chute. Appelé
// APRÈS tryTeleport, pour qu'un téléporteur au bas de la carte prime (le coureur est déplacé avant).
function checkBottomFall(s: GameState, r: Runner): void {
  if (!r.alive || r.y >> 3 !== s.rows - 1) return
  if (r.dashUntil > s.tick) return // dash (Lot D) : franchit les vides (invulnerable pendant le dash)
  const here = tileAt(s, r.x >> 3, r.y >> 3)
  if (isLadder(here) || here === T.Rope) return
  // AUDIT 16/07 : mêmes exemptions pour les tuiles où l'on SÉJOURNE volontairement en dernière rangée —
  // eau/source de bulles (le fond d'un bassin = un lieu, pas une chute ; on y nage, on n'y « tombe » pas)
  // et sable mouvant (on s'y enfonce, la case est un piège en soi). Sans ça, un bassin ou une fosse
  // dessinés jusqu'au bord bas de la carte tuaient « Tombé ! » en pleine nage.
  if (here === T.Water || here === T.Bubbles || here === T.Sand) return
  killRunner(s, r, 'fell')
}

// Rebond du trampoline (tuiles P2 T3) : sous-unites de montee donnees a l'atterrissage sur un
// trampoline. 24 = 3 cases (SUB*3). Consomme en tete de moveRunner, prime sur la gravite.
/** Un trampoline sous les pieds : amplitude du rebond + dérive imposée par la variante DIRECTIONNELLE
 *  (null si la tuile n'est pas un trampoline). Table unique, lue par le coureur ET par le moine
 *  (30/07) : deux listes de tuiles à maintenir en parallèle finissent toujours par diverger. */
export function trampolineOf(tile: T): { sub: number; dir: -1 | 0 | 1 } | null {
  switch (tile) {
    case T.Trampoline: return { sub: BOUNCE_SUB, dir: 0 }
    case T.TrampolineL: return { sub: BOUNCE_SUB, dir: -1 }
    case T.TrampolineR: return { sub: BOUNCE_SUB, dir: 1 }
    case T.TrampolineStrong: return { sub: BOUNCE_STRONG_SUB, dir: 0 }
    case T.TrampolineStrongL: return { sub: BOUNCE_STRONG_SUB, dir: -1 }
    case T.TrampolineStrongR: return { sub: BOUNCE_STRONG_SUB, dir: 1 }
    default: return null
  }
}
const BOUNCE_SUB = 24
// Trampoline PUISSANT (16/07, reste de la demande #7) : 40 = 5 cases. PAIR obligatoire
// (le rebond consomme 2 sous-unites/tick, un impair gelerait la fin de montee).
const BOUNCE_STRONG_SUB = 40

/**
 * LE RESSORT SOUS LES PIEDS ENVOIE EN L'AIR — une seule définition (31/07).
 *
 * Elle vivait en toutes lettres dans la branche d'atterrissage, donc le rebond n'existait QU'EN
 * TOMBANT. Claude-Alain l'a vu sur « Les ressorts » : « quand je passe dessus cela doit générer
 * le bond sans avoir besoin de tomber dessus ». Un ressort sur lequel on marche n'est pas un
 * ressort, et rien à l'écran ne disait pourquoi il ne se passait rien.
 *
 * Rendue commune plutôt que recopiée : la table tuile -> force -> dérive écrite deux fois, c'est
 * exactement ce qui avait rendu le compteur d'or faux.
 *
 * `{ }` = puissant + dérive (C1 16/07 soir) ; `( )` = simple + dérive.
 */
function amorcerRebond(s: GameState, r: Runner): boolean {
  // La reconnaissance des tuiles vit dans `trampolineOf` (ci-dessus), partagée avec les MOINES
  // (30/07) : deux listes de trampolines à tenir à jour en parallèle finissent toujours par
  // diverger. Ici ne reste que ce qui est PROPRE au coureur — son événement de rebond, qui
  // l'identifie par son index et pilote sa pose de saut côté client.
  const tramp = trampolineOf(tileAt(s, r.x >> 3, (r.y >> 3) + 1))
  if (!tramp) return false
  r.bounce = tramp.sub
  r.bounceDir = tramp.dir
  s.events.push({ type: 'bounce', runner: s.runners.indexOf(r) })
  return true
}
/**
 * FAIRE LA PLANCHE (06/08, demande de Claude-Alain après l'enquête trampoline/eau).
 *
 * Il n'existait aucune position de repos dans l'eau : sans commande verticale on coulait, 1
 * sous-unité toutes les deux secondes. Or au joystick — qui ne transmet qu'un axe hors de l'eau —
 * « sans commande verticale » était la situation NORMALE : nager à l'horizontale faisait couler.
 * Arrivé à la surface puis relâché, on retombait au fond en trois secondes, la jauge se vidant.
 * La surface était un piège, pas un abri.
 *
 * Flotter demande trois choses, et la troisième est celle qu'on oublie : être POSÉ sur la rangée
 * de surface (aligné, sinon on est à cheval), ne rien demander à la verticale (tenir « bas » coule
 * toujours, c'est une commande), et surtout avoir la tête DEHORS — la case du dessus doit être de
 * l'air respirable. Sans cette dernière garde, un tunnel immergé sous un plafond de brique
 * laisserait flotter la tête DANS la brique, et y respirer.
 *
 * Sortir complètement de l'eau rend toujours le souffle plein d'un coup : la planche est un repos,
 * pas une apnée gratuite.
 */
function faitLaPlanche(s: GameState, r: Runner, inp: PlayerInput): boolean {
  if (inp.up || inp.down) return false
  // NI EN DASHANT, NI EN PLEIN REBOND. Attrapé par un test existant du Lot 4 (« dasher ne recharge
  // rien ») : sans cette garde, un dash rasant la surface faisait REMONTER le souffle. On ne flotte
  // pas sur le dos en traversant trois cases à la seconde — la planche est un repos, et un repos
  // suppose qu'on ne soit pas en train de faire autre chose.
  if (r.dashUntil > s.tick || r.bounce > 0) return false
  if (r.y % SUB !== 0) return false
  const col = r.x >> 3, row = r.y >> 3
  if (!isWaterT(tileAt(s, col, row))) return false
  // TOUTES les colonnes que le corps chevauche doivent être dégagées, exactement comme l'exige le
  // saut de surface quelques lignes plus haut. Sur une seule colonne, on pouvait flotter à cheval
  // sous le bord d'un surplomb — donc se reposer à un endroit d'où l'on ne peut pas jaillir. Ces
  // deux règles doivent coïncider : « je flotte ici » et « je peux sortir d'ici » disent la même
  // chose au joueur, et c'est ce genre d'écart discret que tout ce chantier corrige.
  const colonnes = r.x % SUB === 0 ? [col] : [col, col + 1]
  return colonnes.every((c) => {
    const dessus = tileAt(s, c, row - 1)
    return !isWaterT(dessus) && !isSolid(dessus) && !crateAt(s, c, row - 1)
  })
}
/** Ce que la planche redonne de souffle par tick. Lent devant la sortie franche (plein d'un coup)
 *  et devant une source de bulles (BREATH_REFILL) : respirer le nez au ras de l'eau se mérite. */
const PLANCHE_REFILL = 1

/**
 * FIN DE BOND : ne réaligner x QUE si la marche reprend la main tout de suite (06/08).
 *
 * L'appel était inconditionnel, et c'était l'à-coup que Claude-Alain sentait : au sommet de l'arc,
 * en apesanteur, le coureur reculait jusqu'à 7 sous-unités d'un coup (mesuré 3 su en dérivant à
 * droite, 6 su à gauche — l'arrondi va toujours vers la gauche, donc le même geste ne rendait pas
 * la même chose des deux côtés). Le petit bond de sortie d'eau subissait le même sort : il sert
 * justement à attraper un rebord, et le recalage l'en écartait.
 *
 * Ce que l'invariant exige vraiment, c'est « on ne MARCHE jamais depuis une position non validée ».
 * Or après un bond on est presque toujours EN L'AIR : la chute sait voler désalignée depuis #103
 * (elle dérive, ou glisse vers l'alignement au relâchement) et `alignXLand` recale à l'arrivée —
 * c'est-à-dire au seul instant où la marche va reprendre. Le recalage du sommet était donc un
 * DOUBLON qui payait un prix visuel sans rien garantir de plus.
 *
 * Reste le cas rare où le bond se termine directement sur un appui (corniche pile à hauteur) :
 * là, la marche reprend vraiment ce tick-ci, et on réaligne comme avant.
 */
function alignSiPorte(s: GameState, r: Runner): void {
  if (hasSupportCenter(s, r)) alignXFloor(r)
}
// EAU (16/07) : rebond de SURFACE — nager vers le haut a la surface propulse hors de l'eau
// (10 su, PAIR) ; le pilotage de rebond existant fait le reste (attraper un bord, retomber).
const SURFACE_JUMP_SUB = 10

// Dash (Lot D) : capacite du coureur. DASH_STEP sous-unites/tick pendant DASH_TICKS ticks = 24 = 3 cases.
const DASH_CHARGE_TICKS = 2 // accroupi de charge (cristal incarné 18/07) : 66 ms avant le burst
const DASH_STEP = 4
const DASH_TICKS = 6
// EXPORTÉ depuis le 31/07 : le bandeau dessine une JAUGE de recharge, il lui faut la durée totale
// pour en calculer le remplissage. Sans ça il aurait fallu la recopier côté client — c'est
// exactement ce qui a rendu le compteur d'or faux (deux écritures de la même règle, une corrigée).
export const DASH_CD_TICKS = 240 // 8 s a 30 Hz : cooldown entre deux dashs (remis a zero par une piece d'or)
export const GEANT_TICKS = 180 // 6 s de forme géant (réglage de la page de design, validé 05/08)
export const DEGUISE_TICKS = 300 // 10 s de déguisement de moine (réglage EXPLICITE de Claude-Alain)
const DEGUISE_GRACE = 15 // la demi-seconde d'immunité bilatérale à la fin (pas de kill « à la frontière »)
export const MAILLET_CD = 10 // cadence du maillet : ~3 coups/s (retour terrain 05/08 « trop lente » — 15 rendait l'arme molle ; le verrou anti-acharnement reste le stun de 0,7 s, pas la cadence)
export const MAILLET_PORTEE = 3 // vol plané de TROIS cases (réglage EXPLICITE de Claude-Alain), stoppé au mur
const MAILLET_STUN = 21 // ~0,7 s d'étourdissement à l'atterrissage (le vol est déjà une trajectoire subie)
export const TOILE_ARMEMENT = 45 // 1,5 s pendant lesquelles la toile épargne SON poseur : le temps de s'écarter, pas plus
export const MOMIE_TICKS = 300 // le cocon d'un JOUEUR ne lâche jamais tout seul avant 10 s : c'est le débattement qui libère (réglage 06/08)
export const MOMIE_COUPS = 8   // GAUCHE-DROITE répété QUATRE fois (g-d-g-d-g-d-g-d), réglage explicite de Claude-Alain
export const MOMIE_MOINE = 150 // un MOINE se débat cinq secondes, tout seul : il n'a pas de manette (06/08)
export const PATATE_MIN = 60, PATATE_JIT = 151 // mèche SECRÈTE de 2 à 7 s (réglage EXPLICITE de Claude-Alain)
const PATATE_PASSE = 15 // anti ping-pong : 0,5 s d'intouchabilité pour celui qui vient de la passer
export const MEUTE_TICKS = 300 // 10 s de meute (réglage EXPLICITE de Claude-Alain)
const MEUTE_LOIN = 6           // les spectres surgissent à SIX cases au moins de la cible : on les voit ARRIVER
export const FANTOME_TICKS = 120 // 4 s d'invisibilité (page de design)
const COURONNE_PERIODE = 30      // une pièce par seconde tant qu'on la porte

function moveRunner(s: GameState, r: Runner, inp: PlayerInput) {
  // RAMPES (16/07) : hop diagonal en cours -> trajectoire VERROUILLEE (1 su x + 1 su y par tick,
  // 8 ticks = 1 case), tout le reste suspendu (motif du saut du moine sauteur). Depart et arrivee
  // ALIGNES : l'invariant « on ne marche jamais depuis une position non validee » est preserve.
  // Vulnerable pendant le hop (checkLava teste la case-centre chaque tick, feu compris).
  if (r.rampHop > 0) {
    r.x += r.rampVx; r.y += r.rampVy; r.rampHop--
    r.face = r.rampVx
    r.falling = false; r.onLadder = false; r.onRope = false
    return
  }
  // CRAPAUD (05/08) : bond d'1 case en cours -> trajectoire VERROUILLÉE, chute suspendue
  // (motif exact du hop du moine sauteur ; l'arc à l'écran est un habillage de rendu pur).
  // Vulnérable pendant le bond, comme pendant un hop de rampe.
  if (r.hop > 0) {
    r.x += r.face; r.hop--
    r.falling = false; r.onLadder = false; r.onRope = false
    return
  }
  let col = r.x >> 3
  const row = r.y >> 3
  let here = tileAt(s, col, row)

  // DASH (Lot D) : capacite active, PRIORITAIRE sur sable/rebond/chute/marche. Declenchement (input
  // dash + CD ecoule + pas deja en dash) puis mouvement horizontal rapide de 3 cases qui IGNORE la
  // gravite et STOPPE au mur. Invulnerable pendant le dash (lave/moine/chute hors ecran ignores, cf.
  // checkLava/checkCollisions/checkBottomFall). Depart aligne -> fin alignee (24 = multiple de 8) :
  // preserve l'invariant "on ne marche jamais depuis une position non validee".
  if (s.dashEnabled) {
    // CRAPAUD : pas de dash sous cette forme (réglage verrouillé Claude-Alain 05/08). Le cooldown
    // continue de vivre : en se décrapotisant, on retrouve son dash là où il en était.
    if (inp.dash && s.tick >= r.dashReadyAt && r.dashUntil <= s.tick && !r.crapaud) {
      r.face = inp.left ? -1 : inp.right ? 1 : r.face
      alignXFloor(r)
      // AUDIT 16/07 : le dash ANNULE un rebond en cours (meme motif que le tapis vertical). Sinon le
      // bounce restait GELE pendant les 6 ticks du dash puis se reconsommait a la sortie = « rebond
      // fantome » (le coureur redecolle tout seul apres un dash, sans trampoline sous lui).
      r.bounce = 0; r.bounceDir = 0
      // CRISTAL INCARNÉ (18/07, maquette B validée) : 2 ticks d'ACCROUPI DE CHARGE avant le burst
      // (l'anticipation qui se savoure). dashUntil couvre charge + burst ; la distance reste 3 cases.
      // L'invulnérabilité dash (dashUntil > tick) couvre donc AUSSI la charge : 2 ticks immobiles
      // offerts, 1 usage / 10 s — assumé. L'event 'dash' part au déclenchement : le glissando
      // cristallin (audio) accompagne la charge puis le burst.
      r.dashChargeUntil = s.tick + DASH_CHARGE_TICKS
      r.dashUntil = s.tick + DASH_CHARGE_TICKS + DASH_TICKS
      r.dashReadyAt = s.tick + DASH_CD_TICKS
      r.rampVy = 0 // RAMPES EN DASH (22/07) : aucune pente en cours (un rampVy residuel d'un vieux hop ne doit jamais faire deriver un dash aerien)
      s.events.push({ type: 'dash', runner: s.runners.indexOf(r) })
    }
    if (r.dashUntil > s.tick) {
      // Gravite ignoree pendant le dash. `falling` reflete l'altitude (mi-case = en l'air) : sinon un
      // dash declenche EN CHUTE finit falling=false desaligne et court-circuite les filets de
      // realignement (gardes par falling) -> coureur FIGE (Critical 1). Ici, a la fin du dash aerien,
      // falling reste true -> le filet "support apparu -> recalage" atterrit proprement.
      r.falling = r.y % SUB !== 0
      if (s.tick < r.dashChargeUntil) return // CHARGE : accroupi, gelé sur place (gravité comprise, 2 ticks)
      const dir = r.face
      const freeD = (c: number, rr: number): boolean => !isSolid(tileAt(s, c, rr)) && !crateAt(s, c, rr)
      for (let i = 0; i < DASH_STEP; i++) {
        // RAMPES EN DASH (22/07, demande Claude-Alain) : segment diagonal en cours (pente posee au
        // centre ci-dessous, meme geometrie que le hop de marche : 1 su x + 1 su y) jusqu'au
        // realignement. rampVy vaut 0 hors pente (pose au declenchement) -> un dash AERIEN
        // (y mi-case constant) ne derive jamais.
        if (r.y % SUB !== 0 && r.rampVy !== 0) { r.x += dir; r.y += r.rampVy; continue }
        // Mur/caisse testés au CENTRE (x%8==0), comme la marche (« au centre on refuse d'entrer ;
        // entre deux cases aucun nouveau mur possible ») -> arrêt NET et ALIGNÉ. #116-1 résidu
        // (16/07 soir) : l'ancien test à la FRONTIÈRE laissait avancer jusqu'à 8k+7 puis
        // alignXFloor RECULAIT de 7 su -> à-coup très visible à l'écran. Tester au centre
        // supprime l'aller-retour sans toucher l'invariant (position d'arrêt = celle de la marche).
        if (r.x % SUB === 0) {
          const nc = (r.x >> 3) + dir
          const rw = r.y >> 3
          const tAhead = tileAt(s, nc, rw)
          if (r.y % SUB === 0) {
            // MONTEE : rampe orientee dans mon sens devant -> diagonale montante (gardes de la marche)
            if (((tAhead === T.RampUpRight && dir === 1) || (tAhead === T.RampUpLeft && dir === -1))
              && freeD(r.x >> 3, rw - 1) && freeD(nc, rw - 1)) {
              r.rampVy = -1; r.x += dir; r.y -= 1; continue
            }
            // TREMPLIN (22/07, « le dash sur une rampe montante doit faire decoller ») : on vient de
            // GRIMPER (segment montant) et la pente s'arrete -> l'elan se convertit en ENVOL : meme
            // arc que le trampoline directionnel (bounce + derive bounceDir vers l'avant), avec son
            // event (son + anim de saut cote client). Le reste de la portee est abandonne.
            if (r.rampVy === -1) {
              r.rampVy = 0
              r.dashUntil = s.tick
              r.bounce = BOUNCE_SUB
              r.bounceDir = dir
              s.events.push({ type: 'bounce', runner: s.runners.indexOf(r) })
              break
            }
            r.rampVy = 0
            if (blocksFrom(tAhead, dir) || crateAt(s, nc, rw)) { r.dashUntil = s.tick; break } // sens unique compris (17/07)
            // DESCENTE : debout sur une rampe orientee bas vers dir -> le dash SUIT la pente.
            // Une CORDE devant a ma hauteur DEVIE la trajectoire : on reste horizontal, on file le
            // long de la corde (et on y finira pendu — meme logique que la marche).
            const sup = tileAt(s, r.x >> 3, rw + 1)
            if (((sup === T.RampUpRight && dir === -1) || (sup === T.RampUpLeft && dir === 1))
              && tAhead !== T.Rope && freeD(nc, rw) && freeD(nc, rw + 1)) {
              r.rampVy = 1; r.x += dir; r.y += 1; continue
            }
          } else if (blocksFrom(tAhead, dir) || crateAt(s, nc, rw)) { r.dashUntil = s.tick; break } // dash aerien : mur au centre (comportement historique)
        }
        r.x += dir
      }
      // TREMPLIN a l'EPUISEMENT : la portee finit PILE en montee (dernier tick du burst, aligne).
      // Si la pente CONTINUE devant (rampe montante praticable), on se pose dessus (pas d'envol a
      // mi-pente) ; sinon on est au SOMMET : l'elan decolle, comme au tremplin en cours de portee.
      if (s.tick >= r.dashUntil - 1 && r.rampVy === -1 && r.x % SUB === 0 && r.y % SUB === 0) {
        const nc2 = (r.x >> 3) + dir, rw2 = r.y >> 3
        const tA2 = tileAt(s, nc2, rw2)
        const upNext = ((tA2 === T.RampUpRight && dir === 1) || (tA2 === T.RampUpLeft && dir === -1))
          && freeD(r.x >> 3, rw2 - 1) && freeD(nc2, rw2 - 1)
        r.rampVy = 0
        if (!upNext) {
          r.bounce = BOUNCE_SUB
          r.bounceDir = dir
          s.events.push({ type: 'bounce', runner: s.runners.indexOf(r) })
        }
      }
      // NB (impasse danger+mur adjacents) : si le dash s'arrete SUR une case dangereuse (lave/moine)
      // parce qu'un mur la borde, l'invuln finit avec le dash et le coureur meurt -> equitable
      // (il a fonce dans un piege sans issue) ; ce n'est pas un artefact d'alignement (meme case).
      return
    }
  }

  // EAU (16/07, spec 2026-07-16-eau-design) : la case-centre est de l'eau -> NAGE. Deplacement
  // libre 4 directions (1 su/tick, diagonale possible), JAMAIS falling dans l'eau (l'entree
  // amortit la chute) ; sans input vertical on COULE lentement (1 su / 2 ticks). Sortie par le
  // haut : a la SURFACE (case au-dessus a l'air libre), nager vers le haut donne un petit
  // rebond (SURFACE_JUMP_SUB) — d'ou la garde bounce===0 : pendant ce rebond, la branche
  // rebond standard pilote (sinon la nage l'annulerait au tick suivant et on ne sortirait
  // jamais). Le souffle (breath) est decompte dans stepRunner ; la noyade y vit aussi.
  if ((here === T.Water || here === T.Bubbles) && r.bounce === 0) {
    r.onLadder = false; r.onRope = false; r.falling = false
    const dx = inp.left ? -1 : inp.right ? 1 : 0
    const dy = inp.up ? -1 : inp.down ? 1 : 0
    if (dx !== 0) r.face = dx
    // Cases chevauchees par le corps (x ou y mi-case) : tout mouvement doit etre praticable
    // sur CHAQUE case chevauchee (ni solide ni caisse), sinon on clipperait un coin.
    const colsSpan = r.x % SUB === 0 ? [col] : [col, col + 1]
    const rowsSpan = r.y % SUB === 0 ? [row] : [row, row + 1]
    const free = (c: number, rr: number): boolean => !isSolid(tileAt(s, c, rr)) && !crateAt(s, c, rr)
    if (dy < 0) {
      if (r.y % SUB === 0 && tileAt(s, col, row - 1) !== T.Water && tileAt(s, col, row - 1) !== T.Bubbles && colsSpan.every((c) => free(c, row - 1))) {
        r.bounce = SURFACE_JUMP_SUB // SURFACE : petit rebond hors de l'eau
        // `waterExit`, PAS `bounce` (06/08). Le mouvement est bien un rebond — il emprunte la même
        // mécanique de montée —, mais il ne doit pas en emprunter la VOIX : ce geste-là est la
        // seule façon de respirer, on le refait sans cesse, et il sonnait comme un trampoline.
        // Changer le type suffit à débrancher d'un coup le boing ET les poses de saut côté client.
        s.events.push({ type: 'waterExit', runner: s.runners.indexOf(r) })
        return
      }
      if (colsSpan.every((c) => free(c, (r.y - 1) >> 3))) r.y -= 1
    } else if (dy > 0) {
      if (colsSpan.every((c) => free(c, (r.y + SUB) >> 3))) r.y += 1
    } else if (s.tick % 2 === 0 && !faitLaPlanche(s, r, inp) && colsSpan.every((c) => free(c, (r.y + SUB) >> 3))) {
      r.y += 1 // coule lentement sans input vertical — sauf en surface, où l'on FLOTTE (06/08)
    }
    if (dx !== 0) {
      const targetCol = dx > 0 ? (r.x + SUB) >> 3 : (r.x - 1) >> 3
      // Porte + clé À LA NAGE (audit 16/07) : on l'ouvre comme à la marche, mais seulement BIEN EN
      // FACE (aligné vertical) — miroir exact de la marche, qui exige d'être aligné en x. Désaligné,
      // la porte reste un mur : aucune clé consommée pour un passage qui n'aurait pas lieu.
      if (r.y % SUB === 0) tryOpenDoor(s, r, targetCol, row)
      // POUSSER UNE CAISSE À LA NAGE (31/07, demande Claude-Alain « je veux pouvoir pousser une
      // caisse métallique sous l'eau »). Sans ce bloc, une caisse était un MUR ABSOLU dans l'eau :
      // la branche nage ne connaissait que `!crateAt`, aucune poussée n'y avait jamais existé.
      //
      // 🚨 LE CRITÈRE N'EST PAS « ALIGNÉ EN Y », et c'est tout le sujet. Un nageur COULE en
      // permanence (1 su / 2 ticks sans input vertical) : il est donc presque toujours À CHEVAL
      // sur deux rangées. Exiger l'alignement — la première version de ce bloc — rendait la
      // poussée quasi impossible en vrai : mesuré, le coureur restait COLLÉ à la caisse sans
      // pouvoir la bouger d'un pouce, exactement le symptôme rapporté. Ça ne marchait que posé
      // au fond, pile aligné, c'est-à-dire dans un test et à peu près jamais en jouant.
      //
      // La règle suit donc EXACTEMENT celle du blocage, deux lignes plus bas : toute caisse qui
      // te barre la route — dans l'une ou l'autre des rangées que ton corps chevauche — est une
      // caisse que tu peux pousser. Ce qui bloque doit pouvoir se pousser, sinon c'est un mur.
      //
      // Le ROCHER est écarté : il se LANCE d'une poussée et écrase ce qu'il croise, une mécanique
      // de terre ferme qu'on ne déclenche pas à bout de bras en apnée.
      if (r.x % SUB === 0) {
        for (const rr of rowsSpan) {
          const cr = crateAt(s, targetCol, rr)
          // ⚠️ La rangée passée à poussePossible est celle de la CAISSE (rr), pas celle du nageur :
          // à cheval, les deux diffèrent, et c'est sous la caisse qu'il faut chercher son appui.
          if (cr && cr.kind !== 'boulder' && poussePossible(s, cr, targetCol, rr, dx as -1 | 1)) {
            cr.vx = dx as -1 | 1; cr.vy = 0; cr.glide = SUB
            cr.pushTick = s.tick
            break // une seule caisse à la fois (Sokoban), même à cheval sur deux rangées
          }
        }
        // Le nageur ne bouge pas ce tick : la caisse occupe encore la case et se libère en
        // glissant à 1 su/tick, soit exactement la vitesse de la nage — ils avancent ensemble.
      }
      // SENS UNIQUE (17/07) : l'entrée à contresens est un mur, aussi à la nage.
      if (rowsSpan.every((rr) => !blocksFrom(tileAt(s, targetCol, rr), dx as -1 | 1) && !crateAt(s, targetCol, rr))) r.x += dx
    }
    return
  }

  // Sable mouvant (Task 10), traité AVANT le support : dès que la case-centre est du
  // sable, l'entité s'enfonce d'1 sous-unité tous les 2 ticks et ne fait RIEN d'autre
  // (ni latéral, ni échelle, ni creusage de mouvement). Elle ne s'enfonce que tant que
  // la case dessous est praticable ; sur un solide dessous elle repose (pas de
  // pénétration). On « entre » dans le sable en tombant dessus ou en y marchant.
  if (here === T.Sand) {
    r.onLadder = false; r.onRope = false; r.falling = false
    if (s.tick % 2 === 0 && !isSolid(tileAt(s, col, row + 1))) { alignXFloor(r); r.y += 1 }
    return
  }
  // Tapis vertical (Lot C) : la case-centre porte le coureur vers le HAUT (ConveyorUp) ou le BAS
  // (ConveyorDown), 1 sous-unite/tick, en surchargeant la gravite (comme le sable/rebond, traite tot).
  // Non solide (on y entre) ; ne pousse pas dans un solide (plafond en montee / sol en descente). x
  // recale (montee/descente verticale pure). Coureur seul en v1 (les moines traversent, non portes).
  if (here === T.ConveyorUp || here === T.ConveyorDown) {
    r.onLadder = false; r.onRope = false; r.falling = false
    r.bounce = 0; r.bounceDir = 0 // le tapis ANNULE un rebond en cours (sinon bounce gele puis reconsomme = rebond fantome)
    // Pilotage lateral (au barreau) : sortir du tapis sur une case adjacente non solide. PRIORITAIRE
    // sur le mouvement vertical -> permet TOUJOURS de quitter le tapis (anti-figement au sommet/fond).
    const dir = inp.left ? -1 : inp.right ? 1 : 0
    if (dir !== 0 && r.y % SUB === 0 && !isSolid(tileAt(s, col + dir, row))) { r.face = dir; r.x += dir; return }
    alignXFloor(r) // sinon : montee/descente verticale pure (x recale)
    if (here === T.ConveyorDown) {
      if (!isSolid(tileAt(s, col, row + 1))) r.y += 1 // dessous libre : descend (au fond/vide -> chute au tick suivant)
      else if (r.y % SUB !== 0) r.y -= 1              // dessous SOLIDE + desaligne (rebond derivant intercepte) :
      // s'aligne vers le haut pour REPOSER sur le solide -> evite le figement desaligne inescapable
      // (Critical revue #4). Dessous solide + deja aligne : repos (rien). N'oscille pas (l'alignement
      // ne joue QUE dessous-solide, jamais pendant une descente libre).
      return
    }
    // ConveyorUp : on monte D'ABORD jusqu'a s'ALIGNER dans la case courante (les sous-pas mi-case
    // restent DANS la case, aucun risque). UNE FOIS ALIGNE, on ne continue QUE si la case du dessus
    // prolonge la montee (autre tapis montant ou echelle) ; sinon on REPOSE aligne au sommet. Ce
    // decalage (arbitrer le plafond a l'alignement seulement) evite le figement desaligne (plafond
    // solide) ET l'oscillation (plafond corde/vide) : le coureur finit toujours aligne, echappable
    // lateralement. Une echelle au-dessus fait transiter vers climb() au tick suivant.
    if (r.y % SUB !== 0) { r.y -= 1; return }
    const above = tileAt(s, col, row - 1)
    // Continue SEULEMENT si le shaft continue (autre tapis montant) OU si le joueur ENGAGE une echelle
    // au-dessus en tenant `haut` (sinon le tapis pousserait dans l'echelle sans input -> climb() exige
    // `haut` pour progresser -> figement desaligne / oscillation). Sans `haut`, repos aligne au sommet.
    if (above === T.ConveyorUp || (isLadder(above) && inp.up)) r.y -= 1
    return
  }
  // Rebond du trampoline : tant qu'il reste du rebond, l'entite MONTE d'1 sous-unite/tick,
  // sauf plafond solide (au centre vertical) qui l'interrompt. Prime sur la gravite.
  if (r.bounce > 0) {
    // Plafond : fin du rebond. On realigne x (la derive en l'air a pu le desaligner) pour garder
    // l'invariant « au sol/porte => x aligne » sur lequel s'appuie la marche (cf. #103, meme classe
    // de bug que la chute dirigeable : sinon marche desalignee -> clip mur).
    if (r.y % SUB === 0 && isSolid(tileAt(s, col, row - 1))) { r.bounce = 0; alignSiPorte(s, r) }
    else {
      // EAU (décision D3 de l'audit 16/07) : l'eau AMORTIT le rebond — 1 sous-unite/tick au lieu de 2
      // tant que la case-centre est immergée. Avant, un rebond traversait un bassin a pleine vitesse,
      // comme s'il n'y avait rien : on « percait » l'eau. Meme histoire physique que le trampoline
      // IMMERGE, qui reste inerte (sans chute, pas d'impact) et que les moines qui PATAUGENT.
      // Le petit rebond de SURFACE n'est pas pénalisé : des sa 1re sous-unite il quitte l'eau.
      const step = here === T.Water || here === T.Bubbles ? 1 : 2
      r.y -= step; r.bounce -= step; r.falling = false // Lot C : rebond plus RAPIDE (2 sous-unites/tick ; BOUNCE_SUB pair)
      if (r.bounce <= 0) {
        r.bounce = 0
        alignSiPorte(s, r) // fin naturelle du rebond : realigner x SEULEMENT si on rend la main a la marche
      } else {
        // pilotage en l'air : le trampoline DIRECTIONNEL (bounceDir, Lot C) amorce une derive par
        // defaut ; l'input du joueur la surcharge (viser une sortie / annuler). Evite le soft-lock.
        const dir = inp.left ? -1 : inp.right ? 1 : r.bounceDir
        if (dir !== 0) {
          r.face = dir
          // case que le centre occuperait apres ce sous-pas, a la rangee COURANTE (post r.y-=1, pas les
          // col/row perimes du haut de moveRunner). Gate par tick -> pas de clip diagonal dans un coin
          // solide (x et y bougent le meme tick), tout en gardant l'anti-soft-lock entre deux colonnes.
          // CHEVAUCHEMENT (audit 17/07) : comme la dérive de chute — le pas est refusé si la
          // nouvelle emprise chevauche un mur/caisse, le rebond reste ALIGNÉ contre les parois.
          const nx = r.x + dir, rr = r.y >> 3
          const okCol = (nc: number): boolean => !blocksFrom(tileAt(s, nc, rr), dir) && !crateAt(s, nc, rr)
          if (okCol(nx >> 3) && okCol((nx + SUB - 1) >> 3)) r.x += dir
        }
      }
      return
    }
  }
  r.onLadder = isLadder(here)
  r.onRope = here === T.Rope && r.y % SUB === 0

  // 1. chute forcée : pas de support -> on descend d'une sous-unité, x reste aligné.
  //    On atterrit (falling=false) dès qu'on redevient aligné ET porté.
  //    SUPPORT « CENTRE » (ressenti « Centre » validé maquette) : le COUREUR décroche dès que son
  //    centre franchit le bord du trou (hasSupportCenter), pas quand ses deux bords lâchent. Les
  //    moines gardent hasSupport (« deux bords ») ailleurs — leur chute ne glisse pas vers le centre.
  if (!hasSupportCenter(s, r) || (r.falling && r.y % SUB !== 0)) {
    // on n'atterrit qu'aligné ; support apparu en cours de chute -> recalage vers le haut
    if (hasSupportCenter(s, r) && r.y % SUB !== 0) {
      r.y &= ~7
      alignXLand(s, r) // #103 : atterrir aligne en x SUR la colonne portee (echelle a cheval sinon oscillation) -> invariant de la marche
      r.falling = false
      return
    }
    // FILET CORDE (22/07, « la trajectoire doit dévier sur la corde ») : au moment du DÉCROCHAGE
    // (marche vers le vide, y aligné), si la case du CENTRE du sprite est une corde DEVANT (dans
    // le sens du pas : inp.right, seule direction où le centre précède la case), on s'y ACCROCHE
    // au lieu de tomber — le geste Lode Runner de marcher de la plateforme sur la corde. C'est ce
    // qui fait dévier la descente d'une rampe sur une corde posée à sa hauteur. JAMAIS en
    // arrière (capture du 22/07 « bloqué au bout de la corde ») : en quittant une corde par son
    // bout gauche, elle traîne encore sous le centre — sans la garde inp.right, le filet la
    // re-snappait à chaque sous-unité et le coureur restait scotché au bout. Vers la gauche, la
    // case du coureur DEVIENT la corde dès l'entrée (x>>3) : hasSupportCenter porte déjà, aucun
    // filet nécessaire — la symétrie est assurée sans lui.
    if (r.y % SUB === 0 && !r.falling && inp.right) {
      const cc = (r.x + SUB / 2) >> 3
      if (cc === (r.x >> 3) + 1 && tileAt(s, cc, r.y >> 3) === T.Rope) {
        r.x = cc << 3
        r.onRope = true
        r.falling = false
        return
      }
    }
    r.falling = true
    // CHUTE DIRIGEE (#103) : le coureur peut deriver lateralement pendant sa chute pour esquiver un
    // danger situe dessous (lave, moine). Motif proche du controle en l'air du rebond trampoline.
    // On ne derive QUE vers une case d'espace de chute reel : la case cible a la rangee courante ET
    // la case en diagonale-bas doivent etre non solides. Cette 2e garde (diagonale-bas) evite de
    // s'incruster dans un solide en biais ET preserve les mecaniques de trou/piege (on ne derive pas
    // hors d'un trou d'1 case borde de sol : on tombe dedans comme avant). La lave n'etant PAS solide,
    // on peut toujours deriver au-dessus d'elle pour l'esquiver.
    const fallDir = inp.left ? -1 : inp.right ? 1 : 0
    const canDrift = (dir: -1 | 1): boolean => {
      // CHEVAUCHEMENT (audit 17/07, « à moitié dans les murs ») : le pas est refusé si la
      // nouvelle emprise [x+dir .. x+dir+7] chevauche une colonne solide/caisse — la dérive
      // s'arrête ALIGNÉE contre un mur, le sprite n'y pénètre plus jamais. (Avant : test à la
      // frontière seule -> collé à 8k+7, 7 px du sprite dans le mur.) La garde diagonale-bas
      // (anti « sortir d'un trou d'1 case ») ne s'applique qu'aux colonnes NOUVELLES : la
      // tester sur sa propre colonne interdirait toute esquive à l'approche de l'atterrissage.
      const nx = r.x + dir, cur = r.x >> 3, nr = r.y >> 3
      const okCol = (nc: number): boolean =>
        !blocksFrom(tileAt(s, nc, nr), dir) && !crateAt(s, nc, nr) && (nc === cur || !isSolid(tileAt(s, nc, nr + 1)))
      const c0 = nx >> 3, c1 = (nx + SUB - 1) >> 3
      return okCol(c0) && (c1 === c0 || okCol(c1))
    }
    // Nerf Lot B : la derive laterale est bridee a ~80% (on saute 1 tick sur 5) -> un peu plus lente
    // que la chute, moins "planante". La chute verticale reste 1 sous-unite/tick.
    const driftAllowed = s.tick % 5 !== 0
    if (fallDir !== 0 && driftAllowed && canDrift(fallDir)) {
      // derive d'1 sous-unite ce tick (x et y bougent le MEME tick), sans recaler x (la derive persiste).
      r.face = fallDir
      r.x += fallDir
      r.y += 1
    } else if (fallDir !== 0 && r.x % SUB !== 0) {
      // Input lateral mais derive impossible ce tick (mur devant OU tick saute par le nerf) ET x deja
      // mi-case : on tombe SANS recaler x. Corrige le "vibre/se colle au mur" (Lot B) : sinon fallOneSub
      // -> alignXFloor snapperait x au debut de la case -> oscillation +7 puis 0 contre le mur.
      r.y += 1
    } else if (fallDir === 0 && r.x % SUB !== 0) {
      // relache en pleine derive : on finit de glisser vers l'alignement (sens r.face) pour eviter un
      // saut brutal de colonne ; si ce pas est bloque, on aligne au sol. Puis chute d'1 sous-unite.
      if (canDrift(r.face)) r.x += r.face
      else alignXFloor(r)
      r.y += 1
    } else {
      fallOneSub(r) // pas d'input (ou input lateral a x deja aligne) -> chute droite historique bit a bit
    }
    // accrochage corde : une entité en chute qui atteint y aligné sur une case
    // Rope s'y accroche (la chute s'arrête au niveau de la corde).
    if (r.y % SUB === 0 && tileAt(s, r.x >> 3, r.y >> 3) === T.Rope) {
      alignXFloor(r) // #103 : s'accrocher a la corde recentre sur la case (la derive a pu desaligner)
      r.falling = false
      r.onRope = true
      return
    }
    if (r.y % SUB === 0 && hasSupportCenter(s, r)) {
      alignXLand(s, r) // #103 : atterrir aligne en x SUR la colonne portee (echelle a cheval sinon oscillation) -> invariant de la marche
      r.falling = false
      // Atterrissage sur un trampoline (case DESSOUS) : amorce le rebond (tuiles P2 T3 + Lot C).
      // Lot C : trampolines DIRECTIONNELS (( = haut-gauche, ) = haut-droite) -> bounceDir amorce une
      // derive laterale par defaut pendant le rebond (le joueur peut la surcharger via son input).
      amorcerRebond(s, r)
    }
    return
  }
  // #103 : si le sol est apparu sous un coureur EN DERIVE (falling + x desaligne) sans qu'il bouge
  // (repousse de trou / crumble / moine piege, avant son mouvement), la branche de chute ci-dessus
  // est sautee en tete de tick -> on realigne x ici aussi, pour garder l'invariant "atterrit aligne".
  if (r.falling) alignXFloor(r)
  r.falling = false

  // 2. échelle : vertical prioritaire ; corde : bas = lâcher (Task 5, no-op ici).
  if (climb(s, r, inp)) return

  // climb() (assist « hitbox échelle ») a pu RECALER r.x sur la colonne voisine puis renvoyer false
  // (ex. bas pressé mais échelle au sol solide). On RECALCULE donc col/here depuis r.x courant avant
  // la marche : sinon le check de mur emploierait une colonne périmée (r.x déjà déplacé) et laisserait
  // passer un pas non validé -> sortie de grille / clip (bug #116-1). No-op si l'assist n'a rien bougé.
  col = r.x >> 3
  here = tileAt(s, col, row)

  // 3. glissade sur glace (Task 10) sinon direction demandée.
  const dir = forcedIceDir(s, r) ?? (inp.left ? -1 : inp.right ? 1 : 0)
  if (dir === 0) return
  r.face = dir
  // RAMPES (16/07), DESCENTE : debout SUR une rampe (le support est la rampe), marcher vers son
  // cote BAS ('/' se descend vers la gauche, '\' vers la droite) = hop diagonal descendant.
  // Conditions : aligne, case traversee (col+dir, row) et case d'arrivee (col+dir, row+1) libres
  // (ni solide ni caisse). Pas d'exigence de support a l'arrivee : sans sol, la chute normale
  // prend le relais au tick suivant (comme un pas classique dans le vide).
  if (r.x % SUB === 0 && r.y % SUB === 0) {
    const sup = tileAt(s, col, row + 1)
    if ((sup === T.RampUpRight && dir === -1) || (sup === T.RampUpLeft && dir === 1)) {
      const ac = col + dir
      const freeR = (c: number, rr: number): boolean => !isSolid(tileAt(s, c, rr)) && !crateAt(s, c, rr)
      // CORDE LE LONG DE LA PENTE (22/07, demande Claude-Alain) : une corde A MA HAUTEUR — ma case
      // (j'y suis pendu) ou la case devant — PRIME sur le plongeon : pas de hop, la marche
      // horizontale continue et la ligne onRope fait le reste (on devie SUR la corde). Pour
      // vraiment descendre depuis la corde : bas (on lache), puis la descente normale reprend.
      if (here !== T.Rope && tileAt(s, ac, row) !== T.Rope && freeR(ac, row) && freeR(ac, row + 1)) {
        r.rampHop = SUB; r.rampVx = dir; r.rampVy = 1
        r.x += dir; r.y += 1; r.rampHop--
        r.falling = false; r.onLadder = false; r.onRope = false
        return
      }
    }
  }
  if (r.y % SUB !== 0) {
    // Assistance échelle (feedback playtest 11/07) : en pleine montée/descente,
    // un input latéral recale au barreau de la case-centre (y &= ~7, qui préserve
    // y>>3 donc tous les prédicats déjà évalués) puis la sortie suit les règles
    // horizontales normales du tick (murs re-vérifiés ci-dessous).
    // TOLÉRANCE SOMMET (#6, 19/07, demande Claude-Alain « pas tout en haut = frustré de ne pas pouvoir
    // aller à gauche/droite ») : en se hissant, le coureur passe DÉSALIGNÉ juste au-dessus du dernier
    // barreau — sa case COURANTE n'est plus une échelle, mais celle du DESSOUS l'est encore. On accepte
    // aussi ce cas : recalé sur la case-centre, il sort sur le palier (ou tombe si c'est le vide, cf.
    // « sortie dans le vide = chute normale voulue »), au lieu de rester coincé juste sous le sommet.
    if (!isLadder(here) && !isLadder(tileAt(s, col, row + 1))) return
    r.y &= ~7
  }

  // Une entité n'occupe qu'une case (x>>3). Au centre (x%8===0) on refuse
  // d'entrer dans une case solide ; entre deux cases (x%8!==0) le mouvement est
  // libre jusqu'au prochain centre (aucun nouveau mur possible avant).
  const targetCol = dir > 0 ? col + 1 : col - 1
  if (r.x % SUB === 0) {
    const crate = crateAt(s, targetCol, row)
    if (crate) {
      // Poussée 1-à-la-fois (Sokoban, Phase 4) : la caisse doit être au REPOS et la case AU-DELÀ
      // libre (ni solide, ni autre caisse). Sinon la caisse est un mur (bloque). Métal ou bois :
      // identique. POIDS (16/07 nuit) : elle ne SAUTE plus d'une case — elle GLISSE, à 1 sous-unité
      // par tick, soit EXACTEMENT la vitesse du coureur : ils avancent donc ensemble, la caisse
      // toujours une case devant (le coureur ne peut entrer dans sa case qu'une fois libérée), et
      // le coureur la repousse pile au tick où elle finit sa case. Aucun téléport, aucun trou.
      // INERTIE RETIRÉE (19/07, demande Claude-Alain « enlever l'inertie des caisses ») : une caisse
      // n'avance QUE tant qu'on la pousse (glide d'une case par poussée) et s'arrête net au relâchement.
      // Seul le ROCHER garde son élan (branche boulder). Le champ `roll` reste dans l'état (rétrocompat
      // des snapshots) mais n'est plus alimenté ni lu.
      const beyondCol = targetCol + dir
      // Sur une PENTE, la caisse t'échappe : on ne la pousse pas, elle dévale (stepCrates s'en charge
      // au même tick). Sans ce garde, la poussée primait sur la pente et on poussait tranquillement
      // une caisse À TRAVERS une rampe, à l'horizontale — l'inverse de ce que la gravité en ferait.
      const sousLaCaisse = tileAt(s, targetCol, row + 1)
      if (sousLaCaisse === T.RampUpRight || sousLaCaisse === T.RampUpLeft) return
      // CRAN D'ARRÊT DU BOUTON (18/07, retour Claude-Alain « je dois pouvoir placer une caisse sur
      // le bouton ») : une caisse arrivée sur T.Button s'y GARE (glideCrate tue élan/roulis et
      // horodate pushTick). Sans cette garde, la poussée MAINTENUE la re-décollait au tick même de
      // son arrivée (coureur collé) : impossible en pratique de la déposer. La re-décoller reste
      // possible — il suffit de pousser encore ~1 s après l'arrivée (aucun softlock).
      if (tileAt(s, targetCol, row) === T.Button && s.tick - crate.pushTick < CRATE_BUTTON_STICK) return
      // ROCHER (17/07) : une seule poussée le LANCE — il roulera tout seul (stepCrates enchaîne
      // les glissements, 2 su/tick) jusqu'à l'obstacle. Pas de garde entityAt : il ÉCRASE ce qui
      // se trouve sur son chemin (c'est sa raison d'être) — le glissement s'en charge.
      if (crate.kind === 'boulder') {
        if (crate.falling || crate.glide > 0 || crate.y % SUB !== 0 || crateWall(tileAt(s, beyondCol, row)) || crateAt(s, beyondCol, row)) return
        crate.vx = dir; crate.vy = 0; crate.glide = SUB
        crate.rollDir = dir
        crate.rollLeft = BOULDER_MOMENTUM // ÉLAN FINI (18/07) : il roulera ~BOULDER_MOMENTUM cases à plat puis s'arrêtera
        s.events.push({ type: 'boulderRoll', cell: cellAt(crate.x, crate.y, s.cols) })
        return // le pousseur ne bouge pas ce tick : le rocher s'éloigne à 2 su/tick, la case se libère devant lui
      }
      // TROU (20/07, retour Claude-Alain « je ne dois pas pouvoir faire passer une caisse PAR-DESSUS un
      // trou ») : on ne pousse pas une caisse qui n'a plus de sol sous elle (trou creusé = case vide).
      // Sans cette garde, le glissement horizontal primait sur la gravité et la caisse « flottait » au
      // -dessus du trou tant qu'on poussait. Privée de support, on la laisse TOMBER dedans (stepCrates).
      // ⚠️ « Avoir un sol » inclut FLOTTER depuis le 31/07 (crateStable) : sans quoi une caisse de
      // bois à la surface de l'eau était impossible à pousser, l'eau n'étant pas un appui solide.
      if (!poussePossible(s, crate, targetCol, row, dir)) return
      crate.vx = dir; crate.vy = 0; crate.glide = SUB
      crate.pushTick = s.tick
    } else if (blocksFrom(tileAt(s, targetCol, row), dir)) {
      // RAMPES (16/07), MONTEE : la « case-mur » est une rampe orientee dans mon sens -> hop
      // diagonal montant au lieu d'un blocage. Conditions : la case au-dessus de MOI et celle
      // au-dessus de la rampe (= arrivee) sont libres (ni solide ni caisse). Mal orientee
      // (dos de la rampe) ou sommet bouche : mur normal (return plus bas).
      const rampT = tileAt(s, targetCol, row)
      if ((rampT === T.RampUpRight && dir === 1) || (rampT === T.RampUpLeft && dir === -1)) {
        const freeR = (c: number, rr: number): boolean => !isSolid(tileAt(s, c, rr)) && !crateAt(s, c, rr)
        if (r.y % SUB === 0 && freeR(col, row - 1) && freeR(targetCol, row - 1)) {
          r.rampHop = SUB; r.rampVx = dir; r.rampVy = -1
          r.x += dir; r.y -= 1; r.rampHop--
          r.falling = false; r.onLadder = false; r.onRope = false
          return
        }
        return // rampe bien orientee mais sommet bouche : bloque comme un mur
      }
      // Porte fermee + cle : on l'ouvre (consomme 1 cle, terrain -> Empty permanent) et on passe.
      if (!tryOpenDoor(s, r, targetCol, row)) return
    }
    // CRAPAUD (05/08) : bond AUTOMATIQUE d'1 case (réglage verrouillé Claude-Alain). Aligné au
    // bord d'un trou d'exactement 1 case — case devant vide SANS appui (un coureur y tomberait),
    // case d'après praticable AVEC appui —, le crapaud bondit au lieu de tomber. Mêmes prédicats
    // que le moine sauteur, mêmes interdits : un bond est un mouvement VOLONTAIRE, il ne transite
    // ni n'atterrit jamais dans la lave ou le feu (sinon le contact du tick suivant le tuerait
    // en plein vol — même invariant que trySauteurHop).
    // LE GÉANT AUSSI (05/08) : il « enjambe les trous d'une case sans les voir » — même verrou
    // de trajectoire, mais en silence (pas d'événement de bond : c'est une simple enjambée).
    if ((r.crapaud || r.geantUntil > s.tick) && r.y % SUB === 0 && !r.onLadder && !r.onRope) {
      const libre = (c: number): boolean => {
        const t = tileAt(s, c, row)
        return !isSolid(t) && t !== T.Lava && t !== T.Fire && !crateAt(s, c, row)
      }
      const landCol = col + 2 * dir
      const midHole = libre(col + dir) && !hasSupport(s, { x: (col + dir) << 3, y: r.y })
      const landOk = libre(landCol) && hasSupport(s, { x: landCol << 3, y: r.y })
      if (midHole && landOk) {
        r.hop = 2 * SUB
        r.x += dir
        r.hop--
        r.falling = false
        if (r.crapaud) s.events.push({ type: 'crapaudHop', runner: s.runners.indexOf(r) })
        return
      }
    }
  }
  r.x += dir
}

// Porte fermée + clé en main : on l'ouvre (consomme 1 clé, terrain -> Empty PERMANENT) et le pas
// se poursuit. Renvoie false si ce n'est pas une porte ou s'il n'y a pas de clé (= mur). Partagé
// par la MARCHE et la NAGE (audit 16/07 : une porte immergée était infranchissable clé en main —
// la branche eau rend la main avant la marche —, donc noyade garantie dans un couloir sous-marin).
function tryOpenDoor(s: GameState, r: Runner, col: number, row: number): boolean {
  if (tileAt(s, col, row) !== T.DoorClosed || r.keys <= 0) return false
  r.keys--
  s.terrain[cellIndex(col, row, s.cols)] = T.Empty
  s.events.push({ type: 'doorOpen', cell: cellIndex(col, row, s.cols) })
  return true
}

// Une chute garde x aligné (contrainte d'axe) : recalage PLANCHER (x &= ~7), qui préserve la
// colonne courante (x>>3) — jamais « au plus proche », qui pourrait changer de case en cours de
// chute et faire rebondir hors d'un trou d'1 case au lieu d'y tomber. (fallOneSub n'est d'ailleurs
// atteint qu'avec x DÉJÀ aligné ; les décrochages à cheval — règle centre du coureur — passent par
// la dérive / le glissé-vers-alignement plus haut, qui l'amènent dans la colonne du trou.)
function fallOneSub(r: { x: number; y: number }) {
  alignXFloor(r)
  r.y += 1
}

// Tapis roulant : une entite alignee au sol et portee par un tapis (case DESSOUS = ConveyorL/R)
// derive d'1 sous-unite/tick dans le sens de la fleche. Au centre d'une case, refuse d'entrer
// dans un solide (mur) ; entre deux cases, la derive continue (aucun nouveau mur avant le centre).
// Pur (aucun RNG). Appele pour coureur ET moine, APRES leur mouvement propre.
//
// `ownStepX` = deplacement horizontal que l'entite vient de faire d'elle-meme ce tick (0 si on ne
// le sait pas / on ne veut pas le faire compter). Quand ce pas s'OPPOSE exactement au tapis, la
// derive est annulee : sinon +1 puis -1 = 0, l'entite n'atteint jamais la case suivante.
// C'est inoffensif pour le COUREUR (appele sans argument : son sur-place face au tapis est le
// ressenti voulu, et le joueur peut renoncer) mais mortel pour le MOINE, dont `chase` ne redecide
// son pas qu'au centre d'une case : il rejouait le meme pas a l'infini et se faisait recracher du
// tapis a reculons, incapable de le traverser (constat Claude-Alain 30/07, mesure : parti de la
// colonne 5, retrouve colonne 2 apres 200 ticks sans jamais avancer).
/** Une case-centre qui PORTE verticalement : tapis montant ou descendant. */
export const isLiftT = (t: T): boolean => t === T.ConveyorUp || t === T.ConveyorDown

/**
 * TAPIS VERTICAL, version partagée (31/07, demande Claude-Alain « je veux que les moines puissent
 * utiliser les tapis de la même façon que moi »). Jusqu'ici les tapis montants/descendants ne
 * portaient QUE le coureur — le code disait « les moines traversent, non portés ».
 *
 * Même contrat que la branche du coureur, aux deux différences près qu'impose l'absence de manette :
 *   - le coureur sort du tapis avec ses FLÈCHES, prioritaires sur la montée. Un moine n'en a pas :
 *     `ownStepX` joue ce rôle — s'il a marché de son propre chef ce tick, sa sortie prime et le
 *     tapis ne le reprend pas. C'est exactement la convention de `conveyorPush` pour l'horizontale.
 *   - le coureur peut engager une ÉCHELLE au sommet en tenant `haut` ; le moine s'arrête au sommet,
 *     aligné, et repart en chasse.
 *
 * ⚠️ Exige `x` ALIGNÉ, comme conveyorPush exige `y` aligné : porter une entité à cheval sur deux
 * colonnes la ferait entrer de biais dans une paroi.
 */
export function conveyorLift(s: GameState, e: { x: number; y: number }, ownStepX = 0): void {
  if (e.x % SUB !== 0) return
  if (ownStepX !== 0) return                        // il a marché : sa sortie latérale prime
  const col = e.x >> 3, row = e.y >> 3
  const here = tileAt(s, col, row)
  if (!isLiftT(here)) return
  if (here === T.ConveyorDown) {
    if (!isSolid(tileAt(s, col, row + 1))) e.y += 1  // dessous libre : il descend
    else if (e.y % SUB !== 0) e.y -= 1               // fond solide + désaligné : se cale DESSUS
    return
  }
  // ConveyorUp : monter d'abord jusqu'à s'aligner dans la case courante, puis ne continuer que si
  // le puits se prolonge. Sinon repos aligné au sommet — jamais figé de biais.
  if (e.y % SUB !== 0) { e.y -= 1; return }
  if (tileAt(s, col, row - 1) === T.ConveyorUp) e.y -= 1
}

export function conveyorPush(s: GameState, e: { x: number; y: number }, ownStepX = 0): void {
  if (e.y % SUB !== 0) return
  const col = e.x >> 3, row = e.y >> 3
  const below = tileAt(s, col, row + 1)
  const dir = below === T.ConveyorL ? -1 : below === T.ConveyorR ? 1 : 0
  if (dir === 0) return
  if (ownStepX !== 0 && Math.sign(ownStepX) === -dir) return // pas contraire : le tapis ne l'annule pas
  if (e.x % SUB === 0 && (isSolid(tileAt(s, col + dir, row)) || crateAt(s, col + dir, row))) return // caisse = mur (Phase 4)
  e.x += dir
}

// APPÂT (31/07, demande Claude-Alain « un objet appât pour que je puisse entraîner un moine dans
// un piège »). Posé SUR PLACE, au centre d'une case portée : un appât flottant en l'air n'aurait
// aucun sens pour un moine qui marche.
//
// IL SE REPREND (31/07, demande Claude-Alain « l'appât doit pouvoir être repris après qu'il soit
// posé »), mais par un geste EXPLICITE — voir reprendreAppat. Il ne rejoint PAS `s.items`, dont les
// objets se ramassent en marchant dessus : on récupérerait alors son propre appât rien qu'en
// traversant le couloir qu'on vient de piéger, ce qui est exactement le trajet qu'on fait.
function poserAppat(s: GameState, r: Runner): void {
  const probe = { x: r.x & ~7, y: r.y, falling: r.falling }
  if (!hasSupport(s, probe)) { s.events.push({ type: 'denied', kind: 'use' }); return }
  const cell = cellAt(probe.x, probe.y, s.cols)
  if (s.appats.includes(cell)) { s.events.push({ type: 'denied', kind: 'use' }); return } // déjà un appât ici
  s.appats.push(cell)
  s.appats.sort((a, b) => a - b) // ordre canonique : le hash d'état ne doit pas dépendre de l'ordre de pose
  r.item = null
  s.events.push({ type: 'appatPose', cell })
}

/** REPRENDRE UN APPÂT POSÉ (31/07, demande Claude-Alain). Mains VIDES, debout sur la case, touche
 *  « utiliser » : le même geste que la pose, dans l'autre sens. Volontairement PAS de ramassage en
 *  marchant dessus — on repasse forcément sur son propre appât en allant tenir l'interrupteur, et
 *  on le récupérerait sans le vouloir en défaisant le piège.
 *  Renvoie true si la pression a servi (reprise ou refus motivé), false s'il n'y avait rien ici. */
function reprendreAppat(s: GameState, r: Runner): boolean {
  if (r.y % SUB !== 0 || r.falling) return false
  const cell = cellAt(r.x & ~7, r.y, s.cols)
  if (!s.appats.includes(cell)) return false
  // Un moine est en train de le manger : on ne le lui arrache pas de la gueule. Sans ce garde, le
  // joueur pourrait annuler le répit à distance de bras et le moine repartirait sans prévenir.
  if (s.monks.some((m) => m.lureCell === cell)) { s.events.push({ type: 'denied', kind: 'use' }); return true }
  s.appats = s.appats.filter((c) => c !== cell)
  r.item = 'appat'
  s.events.push({ type: 'appatRepris', cell })
  return true
}

// Explosion des bombes (Task 11), en TÊTE du tick (avant les trous et les entités,
// cf. l'ordre verrouillé). À `explodeAt` : la bombe est retirée, event `boom`. Les 8
// cases voisines qui sont Brick OU RAMPE deviennent des trous (digAt ; la rampe repousse
// en rampe via regrowTo) ; toute autre tuile (FalseBrick, Concrete, Ladder, Sable, vide
// creusé...) reste intacte, et la case de la bombe elle-même n'est jamais creusée.
// Puis toute entité — coureur, moine vivant OU moine PIÉGÉ (respawnAt === 0) — dont
// la case-centre est dans le carré 3×3 meurt (coureur cause 'bomb', moine killMonk).
// On itère une COPIE de s.bombs car on retire les bombes explosées.
function stepBombs(s: GameState) {
  for (const b of [...s.bombs]) {
    if (s.tick < b.explodeAt) continue
    s.bombs = s.bombs.filter((x) => x !== b)
    souffle(s, b.cell)
  }
}

/** LE SOUFFLE 3×3 (extrait de stepBombs le 05/08 pour la PATATE CHAUDE, qui explose
 *  exactement pareil — « le souffle réutilise le code de la bombe tel quel », page de design).
 *  Comportement bit à bit identique à l'ancien corps de stepBombs : rien n'a bougé, seul le
 *  découpage change, pour qu'il n'existe qu'UNE définition du souffle dans tout le jeu. */
function souffle(s: GameState, cell: number): void {
  {
    s.events.push({ type: 'boom', cell })
    const bc = colOf(cell, s.cols), br = rowOf(cell, s.cols)
    for (let dr = -1; dr <= 1; dr++) for (let dc = -1; dc <= 1; dc++) {
      if (dr === 0 && dc === 0) continue
      const col = bc + dc, row = br + dr
      if (col < 0 || col >= s.cols || row < 0 || row >= s.rows) continue
      const t = tileAt(s, col, row)
      if (t === T.Brick) digAt(s, cellIndex(col, row, s.cols))
      // Briques-rampes (21/07) : soufflées comme les briques, mais le trou REPOUSSE en rampe
      // (regrowTo) pour que la géométrie du niveau se répare à l'identique.
      else if (t === T.RampUpRight || t === T.RampUpLeft) digAt(s, cellIndex(col, row, s.cols), t)
    }
    // Les caisses BOIS du carre 3x3 sont soufflees (16/07) : contenu libere, comme une casse
    // par chute (breakCrate retire de s.crates, d'ou la COPIE). Le metal resiste (indestructible).
    for (const c of [...s.crates]) {
      if (c.kind === 'metal') continue // seul le métal résiste : le bois ET le rocher volent en éclats
      if (Math.abs((c.x >> 3) - bc) <= 1 && Math.abs((c.y >> 3) - br) <= 1) breakCrate(s, c)
    }
    for (const r of s.runners) {
      if (!r.alive) continue
      if (r.dashUntil > s.tick) continue // dash (Lot D) : invulnérable — même au souffle d'une bombe (audit 16/07)
      if (Math.abs((r.x >> 3) - bc) <= 1 && Math.abs((r.y >> 3) - br) <= 1) killRunner(s, r, 'bomb')
    }
    for (const m of s.monks) {
      if (m.respawnAt !== 0 || m.dormant) continue // une STATUE (gong) est de pierre : indestructible
      if (Math.abs((m.x >> 3) - bc) <= 1 && Math.abs((m.y >> 3) - br) <= 1) killMonk(s, m)
    }
    // Ennemis aquatiques (16/07 soir) : soufflés comme les moines, réapparition chez eux.
    for (const f of s.fishes) {
      if (f.respawnAt !== 0) continue
      if (Math.abs((f.x >> 3) - bc) <= 1 && Math.abs((f.y >> 3) - br) <= 1) {
        f.respawnAt = s.tick + MONK_RESPAWN_TICKS
        s.events.push({ type: 'fishDied', cell: cellAt(f.x, f.y, s.cols) })
      }
    }
  }
}

// Utilisation d'un objet (Task 11), appelée APRÈS le creusage et AVANT le mouvement.
// Conditions communes : objet en main, au CENTRE exact d'une case, au sol (aligné,
// porté, pas en chute) — mêmes prédicats que le creusage.
//  - bombe : posée sur la case-centre du coureur (une seule par case ; re-use sur une
//    case déjà minée = ignoré, l'objet RESTE en main), mèche = FUSE_TICKS ticks,
//    event `bombPlaced`. Objet consommé (item = null).
//  - marteau-piqueur : creuse la case ADJACENTE du sens choisi (TOUS SENS, 16/07 soir) —
//    glissé du bouton (useDir) prioritaire, sinon la flèche TENUE au clavier (comme la
//    corde), sinon le BAS (geste historique). Brick -> trou repoussable (digAt), Sable ->
//    vide définitif (digSand) ; toute autre tuile (Concrete, échelle, vide...) = refus et
//    l'objet n'est PAS consommé. Consommé sur succès seul.
function tryUse(s: GameState, r: Runner, inp: PlayerInput) {
  if (!inp.use) { r.useHeld = false; return }
  if (r.useHeld) return
  // CRAPAUD (05/08) : pas de mains — aucun objet ne s'utilise ni ne se reprend sous cette forme.
  // L'objet porté AVANT la transformation reste en poche, prêt pour le retour à la forme humaine.
  if (r.crapaud) { r.useHeld = true; return }
  // GÉANT (05/08) : doigts trop gros — aucun objet ne s'utilise pendant les 6 s (une seconde
  // potion en poche ne se boit donc qu'après le rétrécissement : pas d'enchaînement infini).
  if (r.geantUntil > s.tick) { r.useHeld = true; s.events.push({ type: 'denied', kind: 'use' }); return }
  // DÉGUISEMENT (05/08) : un moine outillé serait absurde — aucun objet pendant les 10 s.
  if (r.deguiseUntil > s.tick) { r.useHeld = true; s.events.push({ type: 'denied', kind: 'use' }); return }
  // MAINS VIDES : le seul geste possible est de REPRENDRE un appât posé sous ses pieds (31/07).
  // ⚠️ `useHeld` n'est armé que si le geste a servi : mains vides sans appât ici, on ne consomme
  // rien et le comportement reste exactement celui d'avant (ramasser un objet en courant, touche
  // déjà enfoncée, doit toujours pouvoir l'utiliser aussitôt).
  if (!r.item) { if (reprendreAppat(s, r)) r.useHeld = true; return }
  r.useHeld = true                              // un geste par pression
  // denied : refus COSMÉTIQUE (event hors hash, zéro RNG, aucun effet sur l'état simulé —
  // le moteur sert le multi + le rejeu). Émis UNE fois par pression (le verrou useHeld
  // ci-dessus garantit un seul passage), sur chaque sortie-sans-action après consommation.
  if (r.item === 'bandelette') {
    // TOILE DE BANDELETTES (refonte du 06/08 sur retour terrain : « on doit pouvoir la poser
    // partout, un peu comme une toile d'araignée, la zone piégée doit être de 2 cases »).
    // AUCUNE exigence de sol ni d'alignement : elle se tend sur une échelle, en pleine chute,
    // au plafond. Elle couvre MA case et celle DEVANT MOI (la face oriente la toile) ; si la
    // case devant est solide, elle se tend derrière — une toile trouve toujours ses deux ancrages.
    const idx = s.runners.indexOf(r)
    const cell = cellAt(r.x + (SUB >> 1), r.y + (SUB >> 1), s.cols)
    const col = colOf(cell, s.cols), row = rowOf(cell, s.cols)
    const libre = (c: number): boolean => c >= 0 && c < s.cols && !isSolid(tileAt(s, c, row))
    const dir = libre(col + r.face) ? r.face : libre(col - r.face) ? -r.face as -1 | 1 : 0
    const cell2 = dir === 0 ? cell : cellIndex(col + dir, row, s.cols)
    // Une seule toile par case : re-poser sur un piège existant est refusé, l'objet RESTE en main.
    if (s.bandelettes.some((b) => b.cell === cell || b.cell2 === cell || b.cell === cell2 || b.cell2 === cell2)) {
      s.events.push({ type: 'denied', kind: 'use' })
      return
    }
    s.bandelettes.push({ cell, cell2, owner: idx, armeAt: s.tick + TOILE_ARMEMENT })
    r.item = null
    s.events.push({ type: 'bandelettePose', cell })
    return
  }
  if (r.y % SUB !== 0 || r.falling) { s.events.push({ type: 'denied', kind: 'use' }); return } // au centre d'une case, pas en chute
  // Tazer : n'exige pas de sol (tire toujours). Glissé du bouton objet (16/07) : useDir
  // gauche/droite choisit le sens du tir SANS se retourner ; sinon, la face (historique).
  if (r.item === 'tazer') { fireTazer(s, r, inp.useDir === 'left' ? -1 : inp.useDir === 'right' ? 1 : r.face); return }
  if (r.item === 'rope') { placeRope(s, r, inp); return } // corde (#4) : pose dans le sens choisi, sans exiger de sol
  if (r.item === 'appat') { poserAppat(s, r); return }    // appât (31/07) : sur place, sol requis (voir plus bas)
  if (r.item === 'meute') {
    // MEUTE PRIVÉE (05/08) : invoquée sans exiger de sol (elle surgit ailleurs, pas sous ses pieds).
    r.item = null
    invoqueMeute(s, r)
    return
  }
  if (r.item === 'fantome') {
    // FANTÔME (05/08) : 4 s d'invisibilité chez les adversaires. Le porteur de la COURONNE ne
    // peut PAS devenir fantôme (la malédiction brille à travers tout) : l'objet est refusé et
    // GARDÉ — pas de gâchis, il servira une fois la couronne perdue.
    if (r.couronne) { s.events.push({ type: 'denied', kind: 'use' }); return }
    r.item = null
    r.fantomeUntil = s.tick + FANTOME_TICKS
    s.events.push({ type: 'fantome', runner: s.runners.indexOf(r) })
    return
  }
  if (r.item === 'deguisement') {
    // DÉGUISEMENT DE MOINE (05/08) : enfilé au moment CHOISI, sans sol requis. Formes
    // exclusives : le masque remplace crapaud et géant (et le drapeau remplacera le masque).
    r.item = null
    r.deguiseUntil = s.tick + DEGUISE_TICKS
    r.crapaud = false
    r.hop = 0
    r.geantUntil = 0
    s.events.push({ type: 'deguise', runner: s.runners.indexOf(r) })
    return
  }
  if (r.item === 'potion') {
    // POTION DE GÉANT (05/08) : bue au moment CHOISI — tout l'objet est là. Pas de sol requis
    // (se boit sur une échelle, comme le tazer se tire). Formes exclusives : la nouvelle
    // remplace l'ancienne (un crapaud qui boit redevient d'abord un coureur... géant).
    r.item = null
    r.geantUntil = s.tick + GEANT_TICKS
    r.crapaud = false
    r.hop = 0
    r.deguiseUntil = 0
    // Dégagement de tête (« jamais de tête coincée dans la brique ») : grandir sous une brique
    // la défonce immédiatement — c'est la règle Q3A, appliquée dès la transformation (le check
    // aligné de stepRunner la rejoue à chaque pas ; ici on couvre la gorgée en pleine case).
    if ((r.y >> 3) > 0 && r.x % SUB === 0 && tileAt(s, r.x >> 3, (r.y >> 3) - 1) === T.Brick) {
      digAt(s, cellIndex(r.x >> 3, (r.y >> 3) - 1, s.cols))
    }
    s.events.push({ type: 'geant', runner: s.runners.indexOf(r) })
    return
  }
  // Même assistance que le creusage : sonde au centre de la case-centre, et le
  // coureur n'est JAMAIS déplacé par le geste (symétrie gauche/droite, 11/07).
  const probe = { x: r.x & ~7, y: r.y, falling: r.falling }
  if (!hasSupport(s, probe)) { s.events.push({ type: 'denied', kind: 'use' }); return } // bombe/marteau : exigent un sol
  const cell = cellAt(probe.x, probe.y, s.cols)
  if (r.item === 'bomb') {
    // Glissé du bouton objet (16/07) : useDir EXPLICITE gauche/droite -> la bombe se pose sur
    // la case ADJACENTE (praticable, PORTÉE par un sol/caisse — jamais de bombe flottante).
    // Tap ou clavier (useDir null) : sur place, comme toujours — la flèche TENUE ne dévie pas
    // la bombe (contrairement à la corde) : on pose souvent en courant.
    const dc = inp.useDir === 'left' ? -1 : inp.useDir === 'right' ? 1 : 0
    const tc = (probe.x >> 3) + dc, tr = probe.y >> 3
    if (dc !== 0 && (isSolid(tileAt(s, tc, tr)) || crateAt(s, tc, tr) !== null ||
      (!isSolid(tileAt(s, tc, tr + 1)) && !crateAt(s, tc, tr + 1)))) {
      s.events.push({ type: 'denied', kind: 'use' })
      return
    }
    const target = dc === 0 ? cell : cellIndex(tc, tr, s.cols)
    if (s.bombs.some((b) => b.cell === target)) { s.events.push({ type: 'denied', kind: 'use' }); return } // déjà une bombe ici : re-use ignoré, objet gardé
    s.bombs.push({ cell: target, explodeAt: s.tick + FUSE_TICKS })
    r.item = null
    s.events.push({ type: 'bombPlaced', cell: target })
  } else if (r.item === 'jackhammer') {
    // TOUS SENS (16/07 soir) : useDir (swipe) prioritaire, sinon flèche tenue, sinon BAS.
    const d = inp.useDir ?? (inp.up ? 'up' : inp.left ? 'left' : inp.right ? 'right' : 'down')
    const tc = (probe.x >> 3) + (d === 'left' ? -1 : d === 'right' ? 1 : 0)
    const tr = (probe.y >> 3) + (d === 'up' ? -1 : d === 'down' ? 1 : 0)
    const target = tileAt(s, tc, tr)
    if (target === T.Brick) { digAt(s, cellIndex(tc, tr, s.cols)); r.item = null }
    else if (target === T.Sand) { digSand(s, cellIndex(tc, tr, s.cols)); r.item = null }
    else { s.events.push({ type: 'denied', kind: 'use' }) } // ni brique ni sable dans ce sens : refus, objet gardé
  }
}

// Objet corde (#4) : pose une tuile Rope dans la case adjacente du SENS choisi — haut prioritaire,
// sinon gauche/droite tenu, sinon la FACE du coureur. Le swipe BAS (16/07 soir) pose DESSOUS
// (utile depuis une échelle au-dessus du vide). Consomme 1 charge (slot vidé à 0). La case
// cible doit être VIDE (sinon refus, objet gardé). Déterministe ; ne déplace jamais le coureur.
function placeRope(s: GameState, r: Runner, inp: PlayerInput): void {
  const col = r.x >> 3, row = r.y >> 3
  // Sens : useDir (tactile #5) prioritaire ; sinon la flèche tenue (clavier) ; sinon la FACE.
  const d = inp.useDir ?? (inp.up ? 'up' : inp.left ? 'left' : inp.right ? 'right' : (r.face < 0 ? 'left' : 'right'))
  const dc = d === 'up' || d === 'down' ? 0 : d === 'left' ? -1 : 1
  const dr = d === 'up' ? -1 : d === 'down' ? 1 : 0
  const tc = col + dc, tr = row + dr
  if (tileAt(s, tc, tr) !== T.Empty) { s.events.push({ type: 'denied', kind: 'use' }); return }
  s.terrain[cellIndex(tc, tr, s.cols)] = T.Rope
  r.charges--
  if (r.charges <= 0) r.item = null
  s.events.push({ type: 'ropePlaced', cell: cellIndex(tc, tr, s.cols) })
}

// Décharge du tazer : éclair horizontal dans le sens du regard, TAZER_RANGE cases, arrêt au
// 1er mur, 1re cible (moine vivant, ou AUTRE joueur en multi : adverse en versus, ALLIÉ en coop
// — décision « tout dangereux », cohérente avec la bombe et le trou qui tuent déjà un allié).
// 1 charge par tir, touche ou non ; slot vidé à 0. Aucun aléa (déterministe).
function fireTazer(s: GameState, r: Runner, dir: -1 | 1) {
  const row = r.y >> 3, col0 = r.x >> 3
  let impact = col0
  for (let n = 1; n <= TAZER_RANGE; n++) {
    const c = col0 + dir * n
    if (c < 0 || c >= s.cols || isSolid(tileAt(s, c, row))) break
    impact = c
    const monk = s.monks.find((m) => m.respawnAt === 0 && !m.dormant && (m.x >> 3) === c && (m.y >> 3) === row)
    if (monk) { stunMonk(s, monk); break } // une STATUE (dormant) ne gaspille pas le tir : l'éclair la traverse
    // Ennemis aquatiques (16/07 soir) : l'éclair traverse l'eau et fige aussi un poisson (rien à lâcher).
    const fish = s.fishes.find((ff) => ff.respawnAt === 0 && (ff.x >> 3) === c && (ff.y >> 3) === row)
    if (fish) { fish.stunUntil = s.tick + STUN_TICKS_MONK; break } // cible = CRÉATURE (poisson) -> 150 t
    if (s.mode === 'versus' || s.mode === 'coop') { // multi « tout dangereux » : fige un adverse (versus) OU un allié (coop) ; solo = jamais d'autre runner
      // GÉANT (05/08) : deux cases de haut — l'éclair le touche aussi À LA TÊTE (pieds à row+1).
      const other = s.runners.find((o) => o !== r && o.alive && (o.x >> 3) === c
        && ((o.y >> 3) === row || (o.geantUntil > s.tick && (o.y >> 3) - 1 === row)))
      if (other) { other.stunUntil = s.tick + STUN_TICKS_PLAYER; break } // cible = JOUEUR (adverse en versus, allié en coop) -> 90 t
    }
  }
  s.events.push({ type: 'taze', from: cellAt(r.x, r.y, s.cols), to: cellIndex(impact, row, s.cols) })
  r.charges--
  if (r.charges <= 0) r.item = null
}

// Étourdit un moine : figé STUN_TICKS_MONK ; s'il portait une pièce il la lâche (case praticable
// stable la plus proche, comme l'éjection d'or d'un trou).
function stunMonk(s: GameState, m: Monk) {
  m.stunUntil = s.tick + STUN_TICKS_MONK // cible = CRÉATURE (moine) -> 150 t
  if (m.carried) {
    m.carried = false
    const to = dropCellFor(s, cellAt(m.x, m.y, s.cols))
    s.golds.push(to); s.golds.sort((a, b) => a - b)
    s.events.push({ type: 'goldDropped', cell: to })
  }
}

// Creusage (Task 6+10). Appelé AVANT le mouvement (contrat : creusage puis mouvement).
// Brique -> trou repoussable (digAt). Sinon, même géométrie latéral-bas mais cible Sand
// (Task 10) -> vide DÉFINITIF sans trou ni repousse (digSand). La signature de canDig est
// verrouillée (cible = Brick uniquement) : le cas sable est testé ici, à l'identique.
function tryDig(s: GameState, r: Runner, inp: PlayerInput) {
  // Un geste par PRESSION (règle 11/07) : maintenir la touche ne re-creuse pas.
  // Sans ce verrou, le recalage se répétait à chaque tick et créait un boost de
  // vitesse asymétrique (entrée de case désalignée en allant à gauche).
  if (!inp.digL && !inp.digR) { r.digHeld = false; return }
  if (r.digHeld) return
  r.digHeld = true // la pression est consommée, geste réussi ou non
  // GÉANT (05/08) : doigts trop gros pour la pioche — refus cosmétique, comme un sol increusable.
  // DÉGUISÉ : un moine qui creuse se démasquerait tout seul — même refus.
  if (r.geantUntil > s.tick || r.deguiseUntil > s.tick) { s.events.push({ type: 'denied', kind: 'dig' }); return }
  const side: -1 | 1 = inp.digL ? -1 : 1
  // denied : refus COSMÉTIQUE (event hors hash, zéro RNG, aucun effet sur l'état simulé —
  // le moteur sert le multi + le rejeu). Émis UNE fois par pression (le verrou digHeld
  // ci-dessus garantit un seul passage), sur chaque sortie-sans-action après consommation.
  if (r.y % SUB !== 0 || r.falling) { s.events.push({ type: 'denied', kind: 'dig' }); return } // en chute/désaligné : refus
  // Sonde à la case du CENTRE du sprite (x+4), PAS au floor du bord gauche : désaligné en
  // marchant, le floor ouvrait le trou côté droit quasi SOUS le coureur (jusqu'à 7/8 du corps
  // au-dessus) et côté gauche à ~2 cases — asymétrie signalée le 16/07. GARDE-FOU (régression
  // signalée le 16/07 après-midi) : la cible ne doit JAMAIS être la case d'APPUI (hasSupport
  // = bord gauche, x>>3) — sinon creuser DERRIÈRE soi en courant (digL désaligné aux 4..7/8,
  // LA mécanique de fuite quand on est poursuivi) ouvrait le trou sous ses propres pieds ->
  // chute immédiate. Dans ce cas la sonde recule sur la case d'appui : le trou s'ouvre une
  // case derrière elle. Propriété finale : le trou latéral est toujours entièrement dégagé du
  // corps ET jamais sous l'appui. Le coureur N'EST JAMAIS déplacé par le geste (règle 11/07).
  const floorCol = r.x >> 3
  let refCol = (r.x + SUB / 2) >> 3
  if (refCol + side === floorCol) refCol = floorCol // cible = case d'appui ? -> on recule d'une case
  const probe = { x: refCol << 3, y: r.y, falling: r.falling }
  // digAnim (event COSMÉTIQUE, hors hash, zéro RNG) : identifie l'auteur d'un creusage RÉUSSI
  // pour la frame d'anim client. Index via indexOf(r), comme death/bounce (r n'a pas son index ici).
  const cell = canDig(s, probe, side)
  if (cell >= 0) { digAt(s, cell); s.events.push({ type: 'digAnim', runner: s.runners.indexOf(r) }); return }
  const col = (probe.x >> 3) + side, row = probe.y >> 3
  const latTile = tileAt(s, col, row) // vide ou CORDE (21/07) : même règle latérale que canDig
  if (hasSupport(s, probe)
    && (latTile === T.Empty || latTile === T.Rope) && tileAt(s, col, row + 1) === T.Sand) {
    digSand(s, cellIndex(col, row + 1, s.cols)); s.events.push({ type: 'digAnim', runner: s.runners.indexOf(r) }); return // sable creusé : succès (le return évite un faux denied ci-dessous)
  }
  s.events.push({ type: 'denied', kind: 'dig' }) // rien de creusable (béton, vide, obstacle surmonté) : refus
}

// Repousse des trous + ensevelissement (Task 6). À tick === regrowAt : la case
// redevient Brick ; toute entité dont la case-centre est ce trou (ou moine piégé
// dedans) meurt. On itère une COPIE de s.holes car on retire les trous repoussés.
function stepHoles(s: GameState) {
  for (const h of [...s.holes]) {
    // REFLUX D'UN REBOUCHAGE ABANDONNÉ (31/07). La terre qu'on a commencé à remettre retombe si
    // l'on s'arrête, deux fois plus vite qu'on ne l'a montée : lâcher coûte le travail fait.
    //
    // 🚨 SANS CECI LE MOINE ÉTAIT EMPRISONNÉ À VIE, et c'est le bug que Claude-Alain a vu. `fill`
    // ne redescendait jamais ; or un trou dont `fill > 0` GÈLE son captif (stepMonks) pour que le
    // joueur qui s'engage ne soit pas pris de vitesse. Un seul coup de pioche donné puis relâché
    // suffisait donc à figer le moine pour toujours — mesuré : toujours dedans 50 s après.
    //
    // ⚠️ Le seuil est `>= 2`, pas `> 0`. stepHoles tourne AVANT stepRunner dans le tick : à `> 0`,
    // le tick en cours défaisait toujours le coup du tick précédent et le rebouchage n'aboutissait
    // jamais. À 2, un maintien continu (cogné au tick N-1) ne reflue pas, un abandon oui.
    if (h.fill > 0 && s.tick - h.fillTick >= 2) h.fill = Math.max(0, h.fill - 2)
    if (s.tick < h.regrowAt) continue
    // TROU-PRISON (31/07, demande Claude-Alain « le trou ne se rebouche plus automatiquement s'il
    // y a un moine à l'intérieur »). Le trou reste ouvert tant qu'il retient quelqu'un : c'est au
    // JOUEUR de venir le combler à la pioche, et ce geste est ce qui tue le captif. On repousse la
    // date de repousse pour qu'à la libération du captif le trou ne claque pas au tick suivant.
    if (monkCaptifDe(s, h.cell)) { h.regrowAt = s.tick + REGROW_TICKS; continue }
    closeHole(s, h)
  }
}

/** Le moine retenu dans ce trou, s'il y en a un (vivant, non statue). */
export function monkCaptifDe(s: GameState, cell: number): Monk | null {
  for (const m of s.monks) if (m.trappedCell === cell && m.respawnAt === 0 && !m.dormant) return m
  return null
}

// REBOUCHAGE À LA PIOCHE (31/07). Contrairement au creusage, ce geste se fait touche MAINTENUE :
// on cogne le bord du trou jusqu'à ce qu'il soit comblé, en FILL_TICKS ticks découpés en
// FILL_COUPS coups visibles. La géométrie visée est EXACTEMENT celle du creusage (case latéral-bas
// côté touche), pour qu'il n'y ait qu'un seul geste à comprendre : la même touche, au même endroit,
// creuse un trou vide et rebouche un trou plein.
// Renvoie true si le tick a servi au rebouchage (le creusage normal est alors sauté).
function tryRebouchage(s: GameState, r: Runner, inp: PlayerInput): boolean {
  if (!inp.digL && !inp.digR) return false
  if (r.geantUntil > s.tick || r.deguiseUntil > s.tick) return false // GÉANT/DÉGUISÉ : la pelle non plus (tryDig émettra le refus)
  if (r.y % SUB !== 0 || r.falling) return false
  const side: -1 | 1 = inp.digL ? -1 : 1
  const floorCol = r.x >> 3
  let refCol = (r.x + SUB / 2) >> 3
  if (refCol + side === floorCol) refCol = floorCol // même recul que tryDig : jamais sous son appui
  const cible = cellIndex(refCol + side, (r.y >> 3) + 1, s.cols)
  const h = s.holes.find((x) => x.cell === cible)
  if (!h) return false
  const avant = Math.floor((h.fill * FILL_COUPS) / FILL_TICKS)
  h.fill++
  h.fillTick = s.tick        // marque le tick : c'est ce qui distingue « on cogne » de « on a lâché »
  const apres = Math.floor((h.fill * FILL_COUPS) / FILL_TICKS)
  if (apres > avant) {
    s.events.push({ type: 'fillStroke', cell: cible, coup: apres }) // un « toc » par coup
    // ANIMATION DU MINEUR (31/07) : on rejoue l'événement d'animation de CREUSAGE à chaque coup.
    // Le geste est le même (il cogne le sol avec sa pioche), le client sait déjà le jouer en deux
    // temps (impact planté puis relevé) — 4 coups annoncés = 4 balancements visibles.
    s.events.push({ type: 'digAnim', runner: s.runners.indexOf(r) })
  }
  if (h.fill >= FILL_TICKS) {
    closeHole(s, h) // comblé : closeHole ensevelit le captif, exactement comme la repousse
    // La pression est CONSOMMÉE : sans ça, la touche encore enfoncée au tick suivant retombait
    // sur tryDig (digHeld valait false, le rebouchage ne passe jamais par lui) et RECREUSAIT
    // aussitôt le trou qu'on venait de combler. Il faut relâcher pour creuser à nouveau.
    r.digHeld = true
  }
  return true
}

// Referme UN trou : éjections (or/objet/caisse), repousse de la tuile, ensevelissement/recalage
// des entités. Extrait de stepHoles (17/07) pour être aussi déclenché PAR LE MOINE BÂTISSEUR
// (fin de chantier) : même mécanique exactement, seul le moment change.
export function closeHole(s: GameState, h: Hole) {
  {
    // Éjection avant la repousse (11/07) : une pièce ou un objet resté dans le
    // trou est déplacé vers une case praticable ET stable — jamais enfermé/perdu.
    const gi = s.golds.indexOf(h.cell)
    if (gi >= 0) {
      const to = dropCellFor(s, h.cell)
      s.golds.splice(gi, 1)
      s.golds.push(to)
      s.golds.sort((a, b) => a - b)
      s.events.push({ type: 'goldDropped', cell: to })
    }
    const it = s.items.find((x) => x.cell === h.cell)
    if (it) it.cell = dropCellFor(s, h.cell)
    // Caisse (Phase 4) restée dans le trou : éjectée sur une case stable AVANT la repousse (sinon
    // encastrée dans le mur régénéré). Comme l'or/les objets ci-dessus.
    const cr = crateAt(s, colOf(h.cell, s.cols), rowOf(h.cell, s.cols))
    if (cr) { const to = centerOf(dropCellFor(s, h.cell), s.cols); cr.x = to.x; cr.y = to.y; cr.falling = false; cr.fallFromRow = -1 }
    s.terrain[h.cell] = h.regrowTo ?? T.Brick   // trous normaux -> Brick ; sol friable -> Crumble (tuiles P1 T3)
    s.events.push({ type: 'regrow', cell: h.cell })
    s.holes = s.holes.filter(x => x !== h)
    // La brique qui repousse ENSEVELIT ce qui est en son centre... et REPOUSSE ce qui ne fait que la
    // chevaucher. AUDIT 16/07 : à cheval sur deux colonnes (x mi-case), une entité dont la case-centre
    // restait libre survivait à MOITIÉ DANS LE MUR — et pouvait ensuite marcher jusqu'au centre du mur
    // et s'y promener. L'ensevelir pour un frôlement serait injuste ; on la recale donc dans sa case,
    // exactement comme la repousse éjecte déjà l'or, les objets et les caisses juste au-dessus.
    for (const r of s.runners) {
      if (!r.alive) continue
      if (cellAt(r.x, r.y, s.cols) === h.cell) {
        if (r.dashUntil <= s.tick) killRunner(s, r, 'buried') // dash (Lot D) : invulnérable, ensevelissement compris (audit)
        continue
      }
      if (r.x % SUB !== 0 && cellIndex((r.x >> 3) + 1, r.y >> 3, s.cols) === h.cell) alignXFloor(r)
    }
    for (const m of s.monks) {
      if (m.respawnAt !== 0 || m.dormant) continue // une STATUE (gong) reste de pierre, même murée
      // EMMURÉ (31/07, demande Claude-Alain « il ne doit pas mourir ») : le CAPTIF d'un trou qu'on
      // rebouche n'est plus enseveli — il reste VIVANT dans la brique, totalement inerte, réduit à
      // deux gros yeux. Un moine qui ne faisait que TRAVERSER la case, lui, est bien écrasé : la
      // brique qui repousse sur un passant reste mortelle, c'est la règle historique.
      if (m.trappedCell === h.cell) {
        m.emmure = true
        m.trappedUntil = CAPTIF
        m.falling = false
        const c = centerOf(h.cell, s.cols)
        m.x = c.x; m.y = c.y                    // recalé au centre : il est DANS la brique
        s.events.push({ type: 'moineEmmure', cell: h.cell })
        continue
      }
      if (cellAt(m.x, m.y, s.cols) === h.cell) { killMonk(s, m); continue }
      if (m.x % SUB !== 0 && cellIndex((m.x >> 3) + 1, m.y >> 3, s.cols) === h.cell) m.x &= ~7
    }
  }
}

// Sol qui s'effrite (tuiles P1 T3). Un coureur ALIGNÉ et porté dont la case DESSOUS est un
// sol friable (Crumble) démarre (une seule fois par case) un minuteur ; à échéance
// (CRUMBLE_TICKS) la case CASSE en trou qui repousse en Crumble (digAt régrowTo=Crumble).
// COMMITTED comme dans Lode Runner : une fois lancé le minuteur va au bout même si le
// coureur part. Pur (aucun RNG). Sans tuile Crumble sous un coureur : ne fait jamais rien.
const CRUMBLE_TICKS = 30 // ~1 s avant que le sol friable cède
function stepCrumble(s: GameState): void {
  for (const r of s.runners) {
    if (!r.alive || r.y % SUB !== 0) continue
    const col = r.x >> 3, row = r.y >> 3
    const belowCell = cellIndex(col, row + 1, s.cols)
    if (s.terrain[belowCell] === T.Crumble && !s.crumble.some(c => c.cell === belowCell)) {
      s.crumble.push({ cell: belowCell, at: s.tick + CRUMBLE_TICKS })
      s.events.push({ type: 'crumble', cell: belowCell })
    }
  }
  for (const c of [...s.crumble]) {
    if (s.tick >= c.at) {
      digAt(s, c.cell, T.Crumble)              // casse -> trou qui repousse en Crumble
      s.crumble = s.crumble.filter(x => x !== c)
    }
  }
}

// Caisses poussables (Phase 4, #2) : gravité + casse + écrasement. Une caisse sans support tombe
// (1 sous-unité/tick, comme les entités). À l'atterrissage : une caisse BOIS tombée de >= 2 cases
// CASSE et lâche son contenu (or -> pot ; objet -> ramassable ; rien -> rien) ; sinon (métal, ou
// bois < 2 cases) elle repose. Pendant sa chute, elle ÉCRASE (tue) tout coureur/moine dont la case
// coïncide avec la sienne (cause 'crushed'). Pur (aucun RNG). Sans caisse : ne fait jamais rien. On
// itère une COPIE de s.crates car la casse en retire.
const CRATE_BREAK_FALL = 2 // casse bois : chute d'au moins 2 cases (décision Claude-Alain 15/07)
function stepCrates(s: GameState): void {
  for (const c of [...s.crates]) {
    const col = c.x >> 3, row = c.y >> 3
    // Lave/feu (16/07, corrige le soir — retour Claude-Alain) : seul le BOIS brule (contenu
    // perdu avec lui — un drop au fond serait inatteignable). Le METAL est indestructible :
    // sur la LAVE il FLOTTE — pose aligne dans la case de SURFACE, il devient un PONT
    // (hasSupport voit deja crateAt) ; dans le FEU (pas un liquide) il passe au travers.
    // Couvre la chute (tuiles non solides, on y entre) ET la poussee laterale.
    const here = tileAt(s, col, row)
    if (here === T.Lava || here === T.Fire) {
      if (c.kind === 'wood') { burnCrate(s, c); continue }
      if (here === T.Lava && c.y % SUB === 0) { if (c.falling) landCrate(s, c, row); continue } // metal a la surface : pose (flotte)
      // metal desaligne dans la lave (s'enfonce jusqu'au centre de la case) ou dans le feu
      // (traverse) : la chute standard ci-dessous continue.
    }
    // GLISSEMENT en cours : PRIORITAIRE sur la gravité, comme le rebond / le sable / le tapis chez le
    // coureur. Une caisse poussée dans le vide finit donc sa case AVANT de tomber — et surtout elle
    // ne s'immobilise JAMAIS mi-case, invariant dont dépendent crateAt, la poussée et le support.
    if (c.glide > 0) { glideCrate(s, c); continue }
    // EAU (31/07, demande Claude-Alain « je veux pouvoir pousser une caisse métallique sous l'eau /
    // la caisse en bois flotte dans l'eau »).
    //
    // Le MÉTAL n'a besoin de RIEN ici : l'eau n'étant pas solide, il coule tout seul par la gravité
    // ci-dessous, et posé au fond il a un support — donc il se pousse exactement comme au sec.
    //
    // Le BOIS FLOTTE. Il remonte tant qu'il est immergé, puis se cale dans la case de SURFACE et y
    // reste : il devient une plateforme praticable, `hasSupport` consultant déjà `crateAt`. C'est
    // le contrat du métal sur la LAVE, en place depuis le 16/07 — une seule idée à comprendre pour
    // les deux liquides, plutôt que deux règles à retenir.
    if (c.kind === 'wood' && isWaterT(here)) {
      // ⚠️ ON NE PASSE PAS PAR landCrate : il BRISE le bois tombé de CRATE_BREAK_FALL cases ou plus.
      // L'eau amortit. Sans ça, une caisse lâchée d'un peu haut se fracasse en touchant l'eau au
      // lieu d'y flotter, et la mécanique demandée n'existe tout simplement pas.
      c.falling = false
      c.fallFromRow = -1
      const auDessus = tileAt(s, col, row - 1)
      const retenue = crateWall(auDessus) || crateAt(s, col, row - 1) !== null
      // Immergée et rien au-dessus : elle remonte, à la vitesse d'une chute (1 su/tick).
      if (isWaterT(auDessus) && !retenue) { c.y -= 1; continue }
      // En surface (ou coincée sous un plafond / une autre caisse) : elle se recale au centre de sa
      // case et flotte. L'invariant « une caisse au repos est TOUJOURS alignée » vaut aussi ici :
      // crateAt, la poussée et le support en dépendent tous les trois.
      if (c.y % SUB !== 0) { c.y -= 1 }
      continue
    }
    if (c.y % SUB === 0) {
      const supported = isSolid(tileAt(s, col, row + 1)) || crateAt(s, col, row + 1) !== null
      if (supported) {
        if (c.falling) { landCrate(s, c, row); continue } // atterrissage : peut casser -> elle glissera au tick suivant
        // PENTES (16/07 nuit) : posée sur une rampe, une caisse ne tient pas — elle DESCEND.
        if (startRampSlide(s, c, col, row)) continue
        // ROCHER (17/07) : en roulement, il ENCHAÎNE les glissements case après case. L'EAU le freine
        // net (il s'y pose, une marche praticable au fond). ÉLAN FINI + REBOND (18/07, retour
        // Claude-Alain « pas d'inertie infinie à plat ; une rampe à contresens change sa direction ») :
        // chaque case à plat entame `rollLeft` (arrêt à 0), et une rampe qui MONTE face à lui (celle
        // que le coureur gravirait dans ce sens) le fait REBONDIR — repart en arrière, élan amputé.
        if (c.kind === 'boulder' && c.rollDir !== 0) {
          if (isWaterT(here)) { c.rollDir = 0; c.rollLeft = 0; continue }
          const ahead = tileAt(s, col + c.rollDir, row)
          const uphill = (ahead === T.RampUpRight && c.rollDir === 1) || (ahead === T.RampUpLeft && c.rollDir === -1)
          if (uphill) {
            c.rollDir = -c.rollDir as -1 | 1
            c.rollLeft = Math.max(0, c.rollLeft - BOULDER_REBOUND_COST) // le rebond coûte de l'élan -> converge vers l'arrêt
            s.events.push({ type: 'boulderRoll', cell: cellAt(c.x, c.y, s.cols) }) // grondement du rebond
          }
          if (c.rollLeft <= 0) { c.rollDir = 0; continue } // élan épuisé : il s'arrête, aligné (plus d'infini à plat)
          if (startBoulderGlide(s, c, col, row, c.rollDir)) { c.rollLeft--; continue }
          c.rollDir = 0; c.rollLeft = 0 // mur/caisse : fin de course, aligné
          continue
        }
        // INERTIE RETIRÉE (19/07) : une caisse relâchée s'arrête NET (le rocher garde son élan via la
        // branche boulder ci-dessus). `c.roll = 0` reste par sécurité pour relire un snapshot d'AVANT
        // le retrait (roll > 0) sans traîner d'état résiduel.
        c.roll = 0
        continue
      }
      if (!c.falling) { c.falling = true; c.fallFromRow = row } // début de chute : mémorise la rangée d'origine
    }
    c.y += 1
    crushUnderCrate(s, c)
  }
}
const CRATE_BUTTON_STICK = 30 // ticks (~1 s de maintien) avant de pouvoir RE-décoller une caisse garée sur un bouton

/** Une caisse est-elle STABLE là où elle est ? Posée sur du solide ou sur une autre caisse — ou
 *  bien en train de FLOTTER : le BOIS sur l'eau (31/07), le MÉTAL sur la lave (16/07).
 *  ⚠️ Sans le cas « flotte », une caisse à flot n'était pas poussable : la seule condition connue
 *  était un appui SOLIDE dessous, et un liquide n'en est pas un. */
function crateStable(s: GameState, c: Crate): boolean {
  const col = c.x >> 3, row = c.y >> 3
  if (isSolid(tileAt(s, col, row + 1)) || crateAt(s, col, row + 1) !== null) return true
  const ici = tileAt(s, col, row)
  if (c.kind === 'wood' && isWaterT(ici)) return true
  return c.kind === 'metal' && ici === T.Lava
}

/** Les conditions de poussée COMMUNES à la marche et à la nage : caisse au repos, alignée, stable,
 *  case au-delà libre, et pas fraîchement garée sur un bouton.
 *  ⚠️ Une seule définition, appelée des deux côtés. La poussée sous l'eau a été ajoutée le 31/07 ;
 *  ré-écrire ces règles dans la branche nage les aurait fait diverger de la terre ferme au premier
 *  réglage — c'est exactement ce qui avait produit le bug du compteur d'or. */
function poussePossible(s: GameState, c: Crate, targetCol: number, row: number, dir: -1 | 1): boolean {
  if (c.falling || c.glide > 0 || c.y % SUB !== 0) return false
  if (!crateStable(s, c)) return false
  if (tileAt(s, targetCol, row) === T.Button && s.tick - c.pushTick < CRATE_BUTTON_STICK) return false
  const beyondCol = targetCol + dir
  return !crateWall(tileAt(s, beyondCol, row)) && crateAt(s, beyondCol, row) === null && !entityAt(s, beyondCol, row)
}
const BOULDER_MOMENTUM = 10 // ROCHER (18/07) : cases de roulement à plat avant l'arrêt — fini, pas infini (retour Claude-Alain)
const BOULDER_REBOUND_COST = 3 // ROCHER (18/07) : élan (cases) perdu à chaque rebond sur une rampe montante -> l'aller-retour converge
// Amorce un glissement d'UNE case dans (vx, vy) si la trajectoire est libre. Renvoie false sinon :
// la caisse reste alors posée, alignée. `entityAt` protège d'un écrasement qu'aucun joueur n'a voulu.
function startGlide(s: GameState, c: Crate, col: number, row: number, vx: -1 | 0 | 1, vy: 0 | 1): boolean {
  if (vx === 0 && vy === 0) return false
  const free = (cc: number, rr: number): boolean =>
    !crateWall(tileAt(s, cc, rr)) && crateAt(s, cc, rr) === null && !entityAt(s, cc, rr)
  if (!free(col + vx, row + vy)) return false
  if (vy !== 0 && !free(col + vx, row)) return false // diagonale : la case TRAVERSÉE doit l'être aussi
  c.vx = vx; c.vy = vy; c.glide = SUB
  return true
}
// Glissement de ROCHER (17/07) : comme startGlide mais SANS la garde entityAt — le rocher
// écrase ce qu'il traverse (crushUnderCrate à chaque sous-pas du glissement s'en charge).
function startBoulderGlide(s: GameState, c: Crate, col: number, row: number, vx: -1 | 1): boolean {
  if (crateWall(tileAt(s, col + vx, row)) || crateAt(s, col + vx, row) !== null) return false
  c.vx = vx; c.vy = 0; c.glide = SUB
  return true
}
// Posée sur une RAMPE : la caisse dévale la pente ('/' descend vers la GAUCHE, '\' vers la DROITE —
// mêmes côtés que le coureur). Diagonale verrouillée de 8 ticks, départ et arrivée alignés : au pied
// de la pente elle repose ou repart en chute, et une volée de rampes s'enchaîne toute seule.
function startRampSlide(s: GameState, c: Crate, col: number, row: number): boolean {
  const sup = tileAt(s, col, row + 1)
  const dir: -1 | 0 | 1 = sup === T.RampUpRight ? -1 : sup === T.RampUpLeft ? 1 : 0
  if (dir === 0) return false
  if (!startGlide(s, c, col, row, dir, 1)) return false
  c.roll = 0 // ce n'est pas une poussée : la pente ne donne pas d'élan de poussée
  if (c.kind === 'boulder') { c.rollDir = dir; c.rollLeft = BOULDER_MOMENTUM } // ROCHER (18/07) : dévaler une pente RELANCE l'élan (la gravité) -> il repart au pied
  return true
}
// Un pas de glissement (1 sous-unité). Si la case d'arrivée s'est fermée entre-temps (autre caisse,
// brique repoussée), la caisse REVIENT à la case d'où elle vient : jamais d'arrêt mi-case.
const crateWall = (t: T): boolean => isSolid(t) || t === T.OneWayR || t === T.OneWayL // un clapet ne laisse JAMAIS passer une caisse
function glideCrate(s: GameState, c: Crate): void {
  // ROCHER (17/07) : il roule VITE — 2 sous-unités/tick à l'horizontale (plus vite que la marche
  // ET que la nage : sur sa trajectoire on l'esquive, on ne le distance pas). Les pentes (vy != 0)
  // restent à 1 su/tick, diagonale verrouillée comme les caisses.
  const steps = c.kind === 'boulder' && c.vy === 0 ? 2 : 1
  for (let i = 0; i < steps && c.glide > 0; i++) {
    const nx = c.x + c.vx, ny = c.y + c.vy
    const ncol = nx >> 3, nrow = ny >> 3
    const blocked = crateWall(tileAt(s, ncol, nrow)) || (crateAt(s, ncol, nrow) !== null && crateAt(s, ncol, nrow) !== c)
    if (blocked) {
      c.x = c.vx > 0 ? c.x & ~7 : (c.x + 7) & ~7 // recalage sur la case de départ
      c.y &= ~7
      c.glide = 0; c.vx = 0; c.vy = 0; c.roll = 0; c.rollDir = 0; c.rollLeft = 0
      return
    }
    c.x = nx; c.y = ny; c.glide--
    if (c.glide === 0) {
      c.vy = 0 // arrivée alignée. `vx` est CONSERVÉ : c'est lui qui dit à l'inertie où repartir.
      // CRAN D'ARRÊT (18/07) : arrivée sur un BOUTON -> la caisse s'y gare. Élan (inertie d'une
      // caisse lancée) et roulis (rocher) meurent ici — point de passage unique de tout glissement.
      // pushTick horodate l'ARRIVÉE : la poussée s'en sert pour refuser le re-décollage immédiat.
      if (tileAt(s, ncol, nrow) === T.Button) { c.roll = 0; c.rollDir = 0; c.rollLeft = 0; c.pushTick = s.tick }
    }
    crushUnderCrate(s, c) // une caisse lancée écrase ce qu'elle traverse (comme une caisse qui tombe)
  }
}
function landCrate(s: GameState, c: Crate, row: number): void {
  c.falling = false
  const height = row - c.fallFromRow
  c.fallFromRow = -1
  if (c.kind === 'wood' && height >= CRATE_BREAK_FALL) breakCrate(s, c)
}
function breakCrate(s: GameState, c: Crate): void {
  s.crates = s.crates.filter((x) => x !== c)
  const cell = cellAt(c.x, c.y, s.cols)
  // L'or d'une caisse est compté dans totalGold DÈS LA CRÉATION (createGame) : le bandeau annonce le
  // pot complet dès le premier tick (« or 0/3 »), la casse ne fait que le rendre ramassable.
  if (c.drop === 'gold') { s.golds.push(cell); s.golds.sort((a, b) => a - b) } // or : entre dans le pot à ramasser
  else if (c.drop) { s.items.push({ cell, kind: c.drop }) }                                    // objet ramassable au sol
  s.events.push({ type: 'crateBreak', cell })
}
// Caisse BRULEE par la lave (16/07) : detruite SANS drop (le contenu brule avec elle).
// Meme event visuel/sonore que la casse (eclats au bon endroit), cosmétique partagé.
function burnCrate(s: GameState, c: Crate): void {
  s.crates = s.crates.filter((x) => x !== c)
  const cell = cellAt(c.x, c.y, s.cols)
  // AUDIT 16/07 — la CLÉ ne brûle pas (c'est du métal) : elle retombe sur la case stable et sûre la
  // plus proche (dropCellFor, comme l'or d'un moine mort). Sans ça, pousser par mégarde une
  // caisse-clé dans la lave scellait DÉFINITIVEMENT toutes les portes du niveau = infinissable, sans
  // le moindre signal. Le reste du contenu (or, bombe, marteau...) brûle bien avec la caisse.
  if (c.drop === 'key') s.items.push({ cell: dropCellFor(s, cell), kind: 'key' })
  s.events.push({ type: 'crateBreak', cell })
}
function crushUnderCrate(s: GameState, c: Crate): void {
  const cell = cellAt(c.x, c.y, s.cols)
  // Dash (Lot D) = invulnérable (cohérent avec lave/moine/chute) : un coureur qui dashe sous une caisse n'est pas écrasé.
  for (const r of s.runners) if (r.alive && r.dashUntil <= s.tick && r.invulnUntil <= s.tick && cellAt(r.x, r.y, s.cols) === cell) killRunner(s, r, 'crushed') // dash ET bouclier de réanimation protègent
  for (const m of s.monks) if (m.respawnAt === 0 && cellAt(m.x, m.y, s.cols) === cell) killMonk(s, m)
}
// Un coureur vivant ou un moine actif occupe-t-il (col,row) ? Empêche de pousser une caisse SUR une
// entité (chevauchement silencieux sans collision) — Phase 4. Ordre stable (déterministe).
function entityAt(s: GameState, col: number, row: number): boolean {
  return s.runners.some((r) => r.alive && (r.x >> 3) === col && (r.y >> 3) === row) ||
    s.monks.some((m) => m.respawnAt === 0 && (m.x >> 3) === col && (m.y >> 3) === row)
}

// Ramassage (Task 7). Au CENTRE exact d'une case seulement : une pièce est prise
// (or++, retirée de golds qui reste trié par splice) ; un objet n'est ramassé que
// les mains vides (item === null), sinon on marche dessus sans le prendre.
function collectAt(s: GameState, r: Runner) {
  // Hitbox élargie (#3) : on ramasse (or, clé, objet) dès que le CENTRE du coureur entre dans une
  // case, sans exiger un alignement PARFAIT -> plus d'or raté en tombant/dérivant en diagonale.
  // Déterministe (fonction du centre entier). Aligné (x%8==0 && y%8==0) : le centre tombe dans la
  // même case -> comportement historique strictement préservé sur les mouvements alignés.
  const cell = cellAt(r.x + (SUB >> 1), r.y + (SUB >> 1), s.cols)
  const gi = s.golds.indexOf(cell)
  if (gi >= 0) {
    s.golds.splice(gi, 1); r.gold++
    s.events.push({ type: 'gold', by: 'runner', cell })
    if (s.dashEnabled) r.dashReadyAt = s.tick // Lot D : une piece d'or remet le cooldown du dash a zero
    wakeStatues(s, cell) // GONG D'OR (17/07) : une statue adjacente à cet or s'éveille
  }
  // Cles : ramassees au contact des la case-centre, comptees a part (n'occupent JAMAIS le slot
  // objet). Sans ce routage, collectAt aspirait la cle dans r.item (findIndex sans filtre kind) et,
  // en solo, stepRespawns ne remet jamais r.item a null -> slot bloque tout le niveau (soft-lock).
  const ki = s.items.findIndex(i => i.cell === cell && i.kind === 'key')
  if (ki >= 0) { s.items.splice(ki, 1); r.keys++; s.events.push({ type: 'keyPicked' }) }
  // LE MAILLET ET LA PIOCHE PLANTÉE (05/08, Q2A + son amendement « plusieurs maillets, un par
  // joueur »). Prendre un maillet au sol PLANTE ta pioche à cette case, foulard au manche ;
  // revenir marcher sur TA pioche fait l'échange inverse (le maillet retombe là). Personne ne
  // vole la pioche d'un autre : elle n'attend que son propriétaire. Aucune forme active ne peut
  // faire l'échange (pas de mains / doigts trop gros / un moine outillé serait absurde).
  const formeActive = r.crapaud || r.geantUntil > s.tick || r.deguiseUntil > s.tick
  if (!formeActive && cell !== r.piocheLock) {
    // Le VERROU (piocheLock) : l'échange plante la pioche SOUS tes pieds — sans lui, debout
    // dessus, prise et rendu se refaisaient à chaque tick. Il se réarme en quittant la case.
    r.piocheLock = -1
    const idx = s.runners.indexOf(r)
    if (!r.maillet) {
      const mi = s.maillets.indexOf(cell)
      if (mi >= 0) {
        s.maillets.splice(mi, 1)
        s.pioches.push({ cell, owner: idx })
        r.maillet = true
        r.piocheLock = cell
        s.events.push({ type: 'malletPris', runner: idx, cell })
      }
    } else {
      const pi = s.pioches.findIndex((p) => p.cell === cell && p.owner === idx)
      if (pi >= 0) {
        s.pioches.splice(pi, 1)
        s.maillets.push(cell)
        s.maillets.sort((a, b) => a - b)
        r.maillet = false
        r.piocheLock = cell
        s.events.push({ type: 'malletRendu', runner: idx, cell })
      }
    }
  }
  // CRAPAUD (05/08) : pas de mains — il gobe l'or et les clés (compteur, aucune main requise),
  // mais ne ramasse AUCUN objet de slot : l'objet reste au sol pour les coureurs humains.
  // DÉGUISÉ (05/08) : pareil — un objet qui disparaît sous un moine sans apparaître au-dessus
  // de sa tête (comme chez les moines armés) trahirait le faux ; il ramasse l'or, comme eux.
  if (r.item === null && !r.crapaud && !(r.deguiseUntil > s.tick)) {
    const ii = s.items.findIndex(i => i.cell === cell && i.kind !== 'key')
    if (ii >= 0) {
      r.item = s.items[ii]!.kind
      r.charges = r.item === 'tazer' ? TAZER_CHARGES : r.item === 'rope' ? ROPE_CHARGES : 0 // tazer/corde arrivent chargés ; bombe/marteau à 0
      s.items.splice(ii, 1)
      s.events.push({ type: 'itemPicked', kind: r.item })
    }
  }
}
// --- LE DRAPEAU-CRAPAUD (05/08, 1er objet versus « les plus fous d'abord ») -----------------
// Un fanion de papier BALADEUR, posé dans l'éditeur (char 'ð'). Il VOLE : porté par le vent, il
// dérive dans les espaces libres, rebondit sur les solides (hors grille = Concrete via tileAt :
// les bords rebondissent tout seuls), et change de cap à intervalles tirés au sort SEEDÉ —
// serveur et clients calculent exactement le même vol, un rejeu aussi. L'attraper (même case)
// transforme le coureur en CRAPAUD à durée indéterminée (sortie : touche creuser, cf. stepRunner) ;
// le drapeau resurgit alors sur sa case d'éditeur et reprend sa balade — plusieurs joueurs
// peuvent donc être crapauds en même temps.
const DRAPEAU_CAP_MIN = 45, DRAPEAU_CAP_JIT = 46 // nouveau cap toutes les 1,5 à 3 s (45..90 ticks)

function stepDrapeaux(s: GameState): void {
  for (const d of s.drapeaux) {
    if (s.tick >= d.turnAt) {
      // 3 tirages par cap, TOUJOURS (nombre de tirages constant par événement — déterminisme) :
      // sens horizontal (jamais 0 : un fanion posé est un fanion mort), dérive verticale (une
      // chance sur deux de voler à plat), délai du prochain cap.
      d.dx = rngInt(s, 2) === 0 ? -1 : 1
      const v = rngInt(s, 4)
      d.dy = v === 0 ? -1 : v === 3 ? 1 : 0
      d.turnAt = s.tick + DRAPEAU_CAP_MIN + rngInt(s, DRAPEAU_CAP_JIT)
    }
    // Le fanion occupe une case (SUB×SUB sous-unités) ; un pas n'est accepté que si AUCUN des
    // quatre coins de la nouvelle emprise n'entre dans un solide — valable à toute position,
    // alignée ou non (il vole, il n'est jamais recalé sur la grille).
    const heurte = (nx: number, ny: number): boolean => {
      const x0 = nx >> 3, x1 = (nx + SUB - 1) >> 3, y0 = ny >> 3, y1 = (ny + SUB - 1) >> 3
      return isSolid(tileAt(s, x0, y0)) || isSolid(tileAt(s, x1, y0))
        || isSolid(tileAt(s, x0, y1)) || isSolid(tileAt(s, x1, y1))
    }
    // Vol : 3 sous-unités sur 4 à l'horizontale (un peu plus lent qu'un coureur -> rattrapable),
    // 1 sur 3 à la verticale (dérive douce). Un mur retourne le sens : il repart en claquant.
    if (s.tick % 4 !== 0) {
      if (!heurte(d.x + d.dx, d.y)) d.x += d.dx
      else d.dx = d.dx === 1 ? -1 : 1
    }
    if (d.dy !== 0 && s.tick % 3 === 0) {
      if (!heurte(d.x, d.y + d.dy)) d.y += d.dy
      else d.dy = d.dy === 1 ? -1 : 1
    }
    // Capture : même case-centre qu'un coureur vivant à forme humaine. Ordre de visite TOURNANT
    // (motif de la boucle d'action du tick) : sur une arrivée simultanée exacte, personne n'est
    // structurellement favorisé. Un seul attrapeur par tick : le drapeau rentre aussitôt chez lui.
    const n = s.runners.length
    for (let k = 0; k < n; k++) {
      const i = (k + s.tick) % n
      const r = s.runners[i]!
      if (!r.alive || r.crapaud) continue
      const rc = cellAt(r.x + (SUB >> 1), r.y + (SUB >> 1), s.cols)
      if (rc !== cellAt(d.x + (SUB >> 1), d.y + (SUB >> 1), s.cols)) continue
      r.crapaud = true
      r.hop = 0
      r.geantUntil = 0 // formes EXCLUSIVES : un géant qui attrape le drapeau redevient... un crapaud
      r.deguiseUntil = 0 // ... et un faux moine démasqué par sa propre gourmandise, pareil
      s.events.push({ type: 'crapaud', runner: i })
      const home = centerOf(d.spawnCell, s.cols)
      d.x = home.x; d.y = home.y
      d.turnAt = s.tick // nouveau cap tiré au prochain tick : il reprend sa balade depuis chez lui
      break
    }
  }
}

// --- LE GÉANT BOUSCULE (05/08, objet versus n°2) --------------------------------------------
// Un géant qui CHEVAUCHE un adversaire (pieds ou tête, il fait deux cases de haut) l'expédie
// en vol plané de DEUX cases — jamais tué : le kill au contact est le territoire du Déguisement.
// Le vol réutilise le bond (r.hop, trajectoire verrouillée) : la distance est validée case par
// case AVANT le départ (1 case si la 2e est bouchée, rien s'il est acculé au mur : on cohabite,
// et on retentera au tick suivant). Ordre de visite TOURNANT, comme partout : aucun index de
// joueur n'est structurellement favorisé quand deux géants se chevauchent en même temps.
function stepGeants(s: GameState): void {
  const n = s.runners.length
  for (let k = 0; k < n; k++) {
    const g = s.runners[(k + s.tick) % n]!
    if (!g.alive || g.geantUntil <= s.tick) continue
    const gc = cellAt(g.x, g.y, s.cols)
    for (let j = 0; j < n; j++) {
      const r = s.runners[(j + s.tick) % n]!
      if (r === g || !r.alive || r.geantUntil > s.tick) continue        // deux géants se traversent
      if (r.invulnUntil > s.tick || r.dashUntil > s.tick || r.hop > 0) continue // bouclier, dash, déjà en vol
      const rc = cellAt(r.x, r.y, s.cols)
      if (rc !== gc && rc !== gc - s.cols) continue
      // Sens de l'expulsion : loin du géant ; à la verticale exacte, sa face décide.
      const dir: -1 | 1 = r.x > g.x ? 1 : r.x < g.x ? -1 : g.face
      const row = r.y >> 3
      const libre = (c: number): boolean => !isSolid(tileAt(s, c, row)) && !crateAt(s, c, row)
      const col = r.x >> 3
      const cases = libre(col + dir) ? (libre(col + 2 * dir) ? 2 : 1) : 0
      if (cases === 0) continue
      alignXFloor(r)                       // départ recalé : le vol plané glisse case par case
      r.face = dir
      r.hop = cases * SUB
      r.x += dir
      r.hop--
      r.falling = false; r.onLadder = false; r.onRope = false
      r.bounce = 0; r.bounceDir = 0        // un rebond en cours n'additionne pas son arc au vol
      s.events.push({ type: 'bouscule', runner: s.runners.indexOf(r) })
    }
  }
}

// --- LA MEUTE PRIVÉE (05/08, objet versus n°7, réglage Claude-Alain : 10 s) ------------------
// Deux moines FANTÔMES surgissent et ne chassent qu'UNE personne : le joueur le plus RICHE à
// l'instant de l'invocation, cible FIGÉE — tout le monde voit le film d'horreur du début à la
// fin. Ils apparaissent à six cases au moins d'elle : on les voit ARRIVER, la terreur est un
// crescendo. Le déguisé n'échappe PAS à la meute (la malédiction voit à travers les robes) ;
// l'invocateur peut parfaitement être le plus riche et se maudire lui-même.
function invoqueMeute(s: GameState, lanceur: Runner): void {
  const idxL = s.runners.indexOf(lanceur)
  // La cible : le plus riche des VIVANTS. Égalité -> le tirage seedé standard du jeu.
  let best = -1, bestOr = -1
  const exaequo: number[] = []
  s.runners.forEach((r, i) => {
    if (!r.alive) return
    if (r.gold > bestOr) { bestOr = r.gold; exaequo.length = 0; exaequo.push(i); best = i }
    else if (r.gold === bestOr) exaequo.push(i)
  })
  if (best < 0) return
  if (exaequo.length > 1) best = tieBreakWinner(s, exaequo)
  // Deux spectres, aux points praticables les plus éloignés de la cible sur sa rangée et la
  // rangée du haut — balayage DÉTERMINISTE (aucun tirage), on prend les deux premiers valides.
  const cible = s.runners[best]!
  const cCol = cible.x >> 3, cRow = cible.y >> 3
  const places: number[] = []
  for (let d = s.cols; d >= MEUTE_LOIN && places.length < 2; d--) {
    for (const dir of [-1, 1]) {
      const col = cCol + dir * d
      if (col < 0 || col >= s.cols) continue
      for (const row of [cRow, 0, cRow - 1, cRow + 1]) {
        if (row < 0 || row >= s.rows) continue
        const t = tileAt(s, col, row)
        if (isSolid(t) || t === T.Lava || t === T.Fire || crateAt(s, col, row)) continue // ni mur ni bûcher : ils surgissent, ils ne se suicident pas
        const cell = cellIndex(col, row, s.cols)
        if (places.includes(cell)) continue
        places.push(cell)
        break
      }
      if (places.length >= 2) break
    }
  }
  for (const cell of places) {
    const m = newMonk(cell, 'normal', s.cols)
    m.spectre = true
    m.cible = best
    m.expireAt = s.tick + MEUTE_TICKS
    s.monks.push(m)
  }
  s.events.push({ type: 'meuteInvoquee', runner: idxL >= 0 ? best : best })
}

// Évaporation des spectres : échéance atteinte, cible morte (leur raison d'être est partie),
// ou tombés dans un trou (le contre-jeu classique du jeu marche contre eux, en mieux : ils n'y
// restent pas captifs, ils s'ÉVAPORENT).
function stepMeute(s: GameState): void {
  if (!s.monks.some((m) => m.spectre)) return
  const partis = s.monks.filter((m) => m.spectre && (
    s.tick >= m.expireAt
    || m.trappedCell >= 0
    || m.respawnAt !== 0
    || !(s.runners[m.cible]?.alive)
  ))
  if (partis.length === 0) return
  for (const m of partis) s.events.push({ type: 'spectreParti', cell: cellAt(m.x, m.y, s.cols) })
  s.monks = s.monks.filter((m) => !partis.includes(m))
}

// --- LA COURONNE MAUDITE (05/08, objet versus n°8) ------------------------------------------
// Qui la coiffe gagne UNE PIÈCE PAR SECONDE... et devient la cible PRIORITAIRE de tous les
// moines réels de la carte. Elle ne se lâche pas : elle ne quitte la tête qu'à la MORT du
// porteur (killRunner la fait rouler d'une case) et attend là le prochain ambitieux.
function stepCouronnes(s: GameState): void {
  if (s.couronnes.length > 0) {
    const n = s.runners.length
    for (let k = 0; k < n; k++) {
      const i = (k + s.tick) % n
      const r = s.runners[i]!
      if (!r.alive || r.couronne) continue
      const cell = cellAt(r.x + (SUB >> 1), r.y + (SUB >> 1), s.cols)
      const ci = s.couronnes.indexOf(cell)
      if (ci < 0) continue
      s.couronnes.splice(ci, 1)
      r.couronne = true
      r.couronneTick = s.tick + COURONNE_PERIODE
      r.fantomeUntil = 0 // « la malédiction brille à travers tout » : pas de fantôme couronné
      s.events.push({ type: 'couronnePrise', runner: i })
    }
  }
  // Le versement : une pièce par seconde, créditée par la sim (score de partie, hors plafonds
  // de la Mine). Le compteur suit le tick, jamais l'horloge : rejeu identique.
  for (const r of s.runners) {
    if (!r.alive || !r.couronne) continue
    if (s.tick < r.couronneTick) continue
    r.couronneTick = s.tick + COURONNE_PERIODE
    r.gold++
    // 🚨 ET LE COMPTEUR DE LA PARTIE AVEC (06/08) — « la couronne ne donne pas de pièce d'or ».
    // Elle en donnait pourtant, et ce gain comptait pour la victoire ; simplement, RIEN NE LE
    // MONTRAIT. Le bandeau n'affiche pas `r.gold` mais `orRamasse` = total du niveau moins ce
    // qui reste à prendre. Une pièce sortie de la couronne ne quitte aucun stock : le compteur
    // restait donc figé, et porter la couronne dix secondes se lisait « elle ne sert à rien ».
    // La couronne CRÉE de la richesse — le total du niveau grandit donc avec elle. La condition
    // de fin n'y touche pas : elle se juge sur `orRestant` (l'or encore À PRENDRE), qui ignore
    // les pièces déjà en main.
    s.totalGold++
  }
}

// --- LA MOMIE DE PAPIER (05/08, objet versus n°5 — déclencheur Q1A choisi par Claude-Alain) --
// Le PIÈGE : une bandelette tendue au sol (posée à la touche action, patron exact de l'appât).
// Le premier ADVERSAIRE qui la traverse est enroulé et la bandelette est consommée ; son poseur
// n'est jamais pris par la sienne (il la voit surlignée, les autres une fine bande discrète).
// Immunisés : le dash, le bouclier de réanimation, le géant (une montagne ne s'enroule pas) et
// celui qui est DÉJÀ momifié. En solo et en coop, la bandelette dort : elle n'a pas d'adversaire.
function stepBandelettes(s: GameState): void {
  // Les MOINES enroulés se libèrent SEULS au bout de cinq secondes (06/08) : ils n'ont pas de
  // manette pour se débattre, alors ils gigotent et finissent par déchirer le rideau.
  for (const m of s.monks) {
    if (m.momieUntil > 0 && s.tick >= m.momieUntil) {
      m.momieUntil = 0
      s.events.push({ type: 'demomifie', runner: -1 }) // -1 : ce n'est pas un coureur
    }
  }
  if (s.bandelettes.length === 0) return
  const n = s.runners.length
  for (const b of [...s.bandelettes]) {
    // TOUT LE MONDE se prend dans la toile (06/08, sa demande) : les moines aussi. Le premier
    // arrivé la consomme, moine ou joueur — un piège ne choisit pas ses victimes. Épargnés :
    // les piégés/statues/emmurés (ils ne marchent pas) et les spectres (ils n'ont pas de corps).
    let pris = false
    for (const m of s.monks) {
      if (m.respawnAt !== 0 || m.trappedCell >= 0 || m.dormant || m.emmure || m.spectre) continue
      if (m.momieUntil > s.tick) continue
      const mc = cellAt(m.x + (SUB >> 1), m.y + (SUB >> 1), s.cols)
      if (mc !== b.cell && mc !== b.cell2) continue
      m.momieUntil = s.tick + MOMIE_MOINE
      m.pathNextCell = -1
      s.events.push({ type: 'momifie', runner: -1 })
      pris = true
      break
    }
    if (pris) { s.bandelettes = s.bandelettes.filter((x) => x !== b); continue }
    for (let k = 0; k < n; k++) {
      const i = (k + s.tick) % n // ordre de visite TOURNANT : aucun index favorisé
      const r = s.runners[i]!
      if (!r.alive) continue
      // ARMEMENT (06/08, retour terrain « on peut passer dessus sans qu'il se passe rien ») :
      // le poseur n'est protégé QUE le temps de s'écarter. Passé ce délai, sa propre toile le
      // prend comme les autres — et surtout, elle fonctionne quand on joue SEUL, ce qui était
      // impossible avec l'ancienne règle (poseur immunisé à vie + versus obligatoire : en
      // solo et en test, marcher dessus ne faisait donc jamais rien).
      if (i === b.owner && s.tick < b.armeAt) continue
      if (r.momieUntil > s.tick || r.geantUntil > s.tick) continue
      if (r.dashUntil > s.tick || r.invulnUntil > s.tick) continue
      const rc = cellAt(r.x + (SUB >> 1), r.y + (SUB >> 1), s.cols)
      if (rc !== b.cell && rc !== b.cell2) continue // la toile couvre DEUX cases
      s.bandelettes = s.bandelettes.filter((x) => x !== b)
      momifie(s, r)
      break
    }
  }
}

/** Enrouler quelqu'un : le cocon, son compte de coups à zéro, et l'alternance ré-armée. */
function momifie(s: GameState, r: Runner): void {
  r.momieUntil = s.tick + MOMIE_TICKS
  r.momieCoups = MOMIE_COUPS
  r.momieCote = 0
  r.bounce = 0; r.bounceDir = 0 // un rebond en cours ne survit pas au cocon
  s.events.push({ type: 'momifie', runner: s.runners.indexOf(r) })
}

/** Libérer : le cocon éclate. Partagé par le débattement, l'échéance et le coup de maillet. */
function demomifie(s: GameState, r: Runner): void {
  r.momieUntil = 0
  r.momieCoups = 0
  r.momieCote = 0
  s.events.push({ type: 'demomifie', runner: s.runners.indexOf(r) })
}

// LE DÉBATTEMENT (05/08) : marteler GAUCHE-DROITE en ALTERNANCE. Appuyer trois fois du même
// côté ne compte pas — il faut G-D-G-D-G-D, le geste physique du « je me débats ». Six coups
// libèrent en une petite seconde ; ne rien faire libère quand même au bout de deux secondes.
// La victime est ACTRICE : c'est ce qui rend la chose drôle au lieu de rageante.
function debatMomie(s: GameState, r: Runner, inp: PlayerInput): void {
  // GAUCHE-DROITE (06/08, sa demande). On accepte les DEUX familles de touches : les flèches de
  // déplacement (le geste naturel — et le seul possible au joystick tactile) comme les touches
  // de pioche. Un côté est un côté : le joueur n'a pas à deviner quelle touche le jeu attend.
  const gauche = inp.left || inp.digL
  const droite = inp.right || inp.digR
  if (!gauche && !droite) { r.digHeld = false; return }
  if (r.digHeld) return
  r.digHeld = true                                   // un coup par PRESSION (verrou partagé avec la pioche)
  const cote: -1 | 1 = gauche ? -1 : 1
  if (cote === r.momieCote) return                   // même côté que le précédent : ça ne compte pas
  r.momieCote = cote
  r.momieCoups--
  s.events.push({ type: 'momieCoup', runner: s.runners.indexOf(r) })
  if (r.momieCoups <= 0) demomifie(s, r)
}

// --- LA PATATE CHAUDE (05/08, objet versus n°6, réglages Claude-Alain) -----------------------
// Posée ÉTEINTE dans l'éditeur : inoffensive, bien visible. Le premier qui la touche l'ALLUME —
// la mèche est tirée au sort SEEDÉ entre 2 et 7 s et reste SECRÈTE (personne ne la connaît, pas
// même le porteur : c'est tout le sel du réglage demandé). Elle se transmet au simple contact,
// avec une demi-seconde d'intouchabilité pour celui qui vient de la passer (sinon deux joueurs
// au corps à corps se la repassent trente fois par seconde). À l'échéance : le souffle d'une
// bombe, 3×3. Si le porteur meurt d'autre chose, elle tombe et FINIT SON CHRONO au sol : la
// zone reste dangereuse, personne n'est forcé de la reprendre.
function stepPatates(s: GameState): void {
  const n = s.runners.length
  for (const p of [...s.patates]) {
    // 1. Elle suit son porteur (ou reste où elle est tombée).
    const porteur = p.porteur >= 0 ? s.runners[p.porteur] : undefined
    if (porteur && porteur.alive) { p.x = porteur.x; p.y = porteur.y }
    else if (p.porteur >= 0) { p.porteur = -1; p.dernier = -1 } // le porteur est mort : elle tombe sur place, chrono en cours
    // 2. L'échéance : le souffle, puis elle disparaît (une patate n'explose qu'une fois).
    if (p.allumee && s.tick >= p.boomAt) {
      s.patates = s.patates.filter((x) => x !== p)
      souffle(s, cellAt(p.x, p.y, s.cols))
      continue
    }
    // 3. Le contact : allumage (éteinte) ou transfert (allumée).
    const pc = cellAt(p.x + (SUB >> 1), p.y + (SUB >> 1), s.cols)
    for (let k = 0; k < n; k++) {
      const i = (k + s.tick) % n // ordre TOURNANT : sur une arrivée simultanée, personne n'est favorisé
      const r = s.runners[i]!
      if (!r.alive || i === p.porteur) continue
      if (i === p.dernier && s.tick - p.passeAt < PATATE_PASSE) continue // anti ping-pong
      if (r.dashUntil > s.tick) continue // le dash traverse tout, la patate comprise
      if (cellAt(r.x + (SUB >> 1), r.y + (SUB >> 1), s.cols) !== pc) continue
      if (!p.allumee) {
        // ⚠️ UN SEUL tirage rng, au moment de l'allumage : le nombre de tirages par événement
        // reste CONSTANT (déterminisme du multi et du rejeu).
        p.allumee = true
        p.boomAt = s.tick + PATATE_MIN + rngInt(s, PATATE_JIT)
        s.events.push({ type: 'patateAllumee', runner: i })
      } else {
        s.events.push({ type: 'patatePassee', runner: i })
      }
      p.dernier = p.porteur
      p.porteur = i
      p.passeAt = s.tick
      p.x = r.x; p.y = r.y
      break
    }
  }
}

// --- LA FRAPPE DU MAILLET (05/08, objet versus n°4 — l'idée de Claude-Alain) -----------------
// Coup dans la case d'à côté : TOUT ce qui s'y trouve (moines ET adversaires) part en VOL PLANÉ
// de trois cases dans le sens du coup — la trajectoire est le bond verrouillé (hop), validée
// case par case AVANT le départ : le vol s'arrête au premier solide (pas de traversée de
// briques), mais il TRAVERSE la lave et le vide (être expédié dans la lave ou dans un trou est
// exactement le destin promis par la page). Étourdi pendant le vol et ~0,7 s après (les moines
// étourdis GLISSENT leur bond avant de subir la gravité, cf. stepMonks). Immunisés : le dash,
// le bouclier de réanimation, le GÉANT (une montagne ne se bump pas), les moines piégés/statues
// (on ne frappe pas la pierre ni un captif au fond d'un trou).
function frappeMaillet(s: GameState, r: Runner, side: -1 | 1): void {
  r.face = side
  s.events.push({ type: 'malletSwing', runner: s.runners.indexOf(r) })
  const row = r.y >> 3
  // ZONE de la frappe (correctif 05/08, retour Claude-Alain « je n'arrive pas à bump ») : le
  // coup couvre MA case ET la case visée. La première version ne touchait que la case d'à
  // côté : un moine lancé la traversait ENTRE deux coups (cadence 15 ticks, ~10 ticks par
  // case), et un moine qui me chevauchait déjà était infrappable — mesuré : 5 swings, 0
  // touche, mort du joueur. Le balayage d'un maillet couvre évidemment le corps à corps.
  const probeCol = (r.x + (SUB >> 1)) >> 3
  const zone = [cellIndex(probeCol, row, s.cols), cellIndex(probeCol + side, row, s.cols)]
  const bloque = (c: number): boolean => isSolid(tileAt(s, c, row)) || crateAt(s, c, row) !== null
  // Le vol se mesure DEPUIS la victime (deux victimes de la zone ne volent pas pareil) :
  // jusqu'à 3 cases, stoppé au premier solide — jamais par la lave ni les trous.
  const volDepuis = (col: number): number => {
    let n = 0
    while (n < MAILLET_PORTEE && !bloque(col + side * (n + 1))) n++
    return n
  }
  for (const m of s.monks) {
    if (m.respawnAt !== 0 || m.trappedCell >= 0 || m.dormant || m.emmure) continue
    const mc = cellAt(m.x + (SUB >> 1), m.y + (SUB >> 1), s.cols) // centre du corps, hitbox généreuse
    if (!zone.includes(mc)) continue
    const cases = volDepuis(colOf(mc, s.cols))
    m.face = side
    m.hop = cases * SUB
    m.stunUntil = s.tick + cases * SUB + MAILLET_STUN
    // Un coup de maillet libère un moine ENROULÉ, comme il libère une momie humaine — et
    // l'envoie voler quand même. Rendre service en assommant, c'est la règle de la maison.
    if (m.momieUntil > s.tick) { m.momieUntil = 0; s.events.push({ type: 'demomifie', runner: -1 }) }
    // LE CHOC LUI FAIT LÂCHER SA PIÈCE (06/08, demande Claude-Alain) : même geste que le tazer
    // (dropCellFor, case praticable stable la plus proche). Reprendre son or au voleur d'un coup
    // de maillet devient une raison de plus de porter l'arme.
    if (m.carried) {
      m.carried = false
      const to = dropCellFor(s, cellAt(m.x, m.y, s.cols))
      s.golds.push(to); s.golds.sort((a, b) => a - b)
      s.events.push({ type: 'goldDropped', cell: to })
    }
    s.events.push({ type: 'malletHit', cell: mc })
  }
  // CE QUE LE MAILLET DÉCLENCHE DANS LE MONDE (06/08, « le maillet doit pouvoir déclencher des
  // actions dans le jeu, à toi de les trouver »). Une seule règle, qui les explique toutes :
  // LE MAILLET EXPULSE OU DÉFONCE TOUT CE QUI N'EST PAS ANCRÉ. D'où, dans l'ordre de la zone :
  //   · une CAISSE part en glissade comme un rocher lancé (le bois éclate contre le mur) ;
  //   · une BOMBE amorcée est RENVOYÉE trois cases — retourner la bombe de l'adversaire ;
  //   · une PATATE chaude au sol repart pareil (personne ne veut la garder) ;
  //   · l'OR au sol est projeté : on peut se servir à distance, ou en priver quelqu'un ;
  //   · une PORTE fermée est DÉFONCÉE sans clé : le maillet ouvre des routes.
  for (const c of zone) {
    const col = colOf(c, s.cols)
    // La porte : elle cède, définitivement, exactement comme si on l'avait ouverte à la clé.
    if (tileAt(s, col, row) === T.DoorClosed) {
      s.terrain[c] = T.Empty
      s.events.push({ type: 'doorOpen', cell: c })
      continue
    }
    const cases = volDepuis(col)
    if (cases === 0) continue
    const dest = cellIndex(col + side * cases, row, s.cols)
    // La caisse : lancée comme un rocher (glide + élan). Le reste de sa vie est déjà écrit
    // dans stepCrates : elle roule, écrase, casse en tombant. On ne fait que la mettre en route.
    const crate = crateAt(s, col, row)
    if (crate && !crate.falling && crate.glide === 0 && crate.y % SUB === 0) {
      crate.vx = side; crate.vy = 0; crate.glide = SUB
      crate.rollDir = side
      crate.rollLeft = cases
      s.events.push({ type: 'boulderRoll', cell: c })
      continue
    }
    // La bombe amorcée : renvoyée (sa mèche continue de brûler — c'est tout le sel).
    const bi = s.bombs.findIndex((b) => b.cell === c)
    if (bi >= 0 && !s.bombs.some((b) => b.cell === dest)) {
      s.bombs[bi]!.cell = dest
      s.events.push({ type: 'malletHit', cell: c })
      continue
    }
    // La patate chaude POSÉE (pas celle qu'on porte : celle-là part avec son dos).
    const pat = s.patates.find((p) => p.porteur < 0 && cellAt(p.x + (SUB >> 1), p.y + (SUB >> 1), s.cols) === c)
    if (pat) {
      const dep = centerOf(dest, s.cols)
      pat.x = dep.x; pat.y = dep.y
      s.events.push({ type: 'malletHit', cell: c })
      continue
    }
    // L'or au sol : projeté au bout de la portée (settleLoose le fera retomber s'il n'y a pas
    // de sol là-bas — le maillet lance, la gravité conclut).
    const gi = s.golds.indexOf(c)
    if (gi >= 0) {
      s.golds.splice(gi, 1)
      s.golds.push(dest)
      s.golds.sort((a, b) => a - b)
      s.events.push({ type: 'goldDropped', cell: dest })
    }
  }
  for (const o of s.runners) {
    if (o === r || !o.alive) continue
    if (o.dashUntil > s.tick || o.invulnUntil > s.tick || o.geantUntil > s.tick) continue
    const oc = cellAt(o.x + (SUB >> 1), o.y + (SUB >> 1), s.cols)
    if (!zone.includes(oc)) continue
    const cases = volDepuis(colOf(oc, s.cols))
    alignXFloor(o)
    o.face = side
    o.hop = cases * SUB
    o.stunUntil = s.tick + cases * SUB + MAILLET_STUN
    o.bounce = 0; o.bounceDir = 0
    o.onLadder = false; o.onRope = false
    // UN COUP DE MAILLET SUR UNE MOMIE LA LIBÈRE (05/08, page de design : « les objets se
    // parlent entre eux ») — et elle s'envole quand même. Rendre service en assommant, voilà.
    if (o.momieUntil > s.tick) demomifie(s, o)
    s.events.push({ type: 'malletHit', cell: oc })
  }
}

// --- LE DÉGUISEMENT TUE AU CONTACT (05/08, objet versus n°3, exigence Claude-Alain) ----------
// « je dois pouvoir kill mes adversaires en pop juste en les touchant ». Chevauchement de
// cases (la détection du gant), mort STANDARD (réapparition + pièce lâchée par killRunner).
// VERSUS seulement : en coop et en solo le masque reste un outil de discrétion (les moines
// l'ignorent), jamais une arme contre les alliés. Immunisés : le dash (il traverse tout),
// le bouclier de réanimation, le GÉANT (immunisé aux moines — faux compris), et les autres
// faux moines (un moine ne tue pas un moine).
function stepDeguises(s: GameState): void {
  if (s.mode !== 'versus') return
  const n = s.runners.length
  for (let k = 0; k < n; k++) {
    const d = s.runners[(k + s.tick) % n]!
    if (!d.alive || d.deguiseUntil <= s.tick) continue
    const dc = cellAt(d.x, d.y, s.cols)
    for (let j = 0; j < n; j++) {
      const r = s.runners[(j + s.tick) % n]!
      if (r === d || !r.alive) continue
      if (r.deguiseUntil > s.tick || r.geantUntil > s.tick) continue
      if (r.dashUntil > s.tick || r.invulnUntil > s.tick) continue
      if (cellAt(r.x, r.y, s.cols) !== dc) continue
      killRunner(s, r, 'monk') // pop : aux yeux de la victime, un moine l'a eue — c'est vrai, en un sens
    }
  }
}

// Échelles et cordes (contrat Task 5). Renvoie true si le tick est consommé par
// un déplacement vertical (l'horizontal est alors ignoré : vertical > horizontal).
function climb(s: GameState, r: Runner, inp: PlayerInput): boolean {
  let col = r.x >> 3
  const row = r.y >> 3
  const aligned = r.y % SUB === 0
  // Assist « hitbox échelle » (12/07) : à l'arrêt vertical, si le coureur presse haut/bas sans
  // être centré sur une échelle mais que son SPRITE EN CHEVAUCHE une dans la colonne voisine
  // (il est désaligné horizontalement, footprint = 2 colonnes), on l'y recale. Rend l'accroche
  // SYMÉTRIQUE : avant, on ne s'accrochait qu'à la colonne du bord gauche (x>>3), donc facile en
  // arrivant par la droite mais dur par la gauche (le sprite touchait l'échelle sans « prise »).
  // N'agit que sur intention (haut/bas) ET centré verticalement (jamais en pleine chute).
  if (
    aligned && (inp.up || inp.down) && r.x % SUB !== 0 &&
    !isLadder(tileAt(s, col, row)) && isLadder(tileAt(s, col + 1, row))
  ) {
    r.x = (col + 1) * SUB
    col += 1
  }
  const here = tileAt(s, col, row)
  const below = tileAt(s, col, row + 1)
  const onLadderTile = isLadder(here)

  // Montée
  if (inp.up) {
    if (onLadderTile) {
      if (!aligned) { r.y -= 1; return true } // poursuite jusqu'à l'alignement
      const above = tileAt(s, col, row - 1)
      if (isSolid(above)) return true // plafond : bloqué, mais l'input vertical est consommé
      if (isLadder(above)) { alignXFloor(r); r.y -= 1; return true }
      // Sommet (AUDIT 17/07, demande Claude-Alain « monter sur le haut de l'échelle ») :
      // la case au-dessus est praticable -> on se HISSE dessus, TOUJOURS. Le coureur debout
      // au-dessus du dernier barreau est PORTÉ (hasSupport : échelle dessous) : stable, on en
      // sort en marchant (et on tombe si on marche dans le vide — voulu). L'ancienne règle
      // exigeait une corde ou un palier solide adjacent : une échelle-tour restait
      // inaccessible à son sommet, le coureur figé sous le dernier barreau.
      alignXFloor(r); r.y -= 1; return true
    }
    // Sorti au-dessus du sommet mais encore désaligné : finir de monter au palier.
    if (!aligned && isLadder(below)) { r.y -= 1; return true }
  }

  // Descente (échelle) et corde
  if (inp.down) {
    if (r.onRope) {
      // Depuis une corde : descendre dans une échelle en dessous = autorisé (contrat) ;
      // sur un solide = no-op (on NE lâche PAS, sinon lâcher/rattrapage = vibration).
      if (isLadder(below)) { r.onRope = false; alignXFloor(r); r.y += 1; return true }
      if (isSolid(below)) return true
      r.onRope = false; r.falling = true; fallOneSub(r); return true // sinon : lâcher (chute)
    }
    if (onLadderTile && !isSolid(below)) { alignXFloor(r); r.y += 1; return true }
    if (aligned && isLadder(below)) { alignXFloor(r); r.y += 1; return true } // du sol vers l'échelle dessous
    if (!aligned && isLadder(below)) { r.y += 1; return true } // poursuite de la descente
  }

  return false
}


// Recale x sur la COLONNE VALIDÉE (plancher = x>>3, la case-centre sur laquelle
// climb() et la chute évaluent leurs prédicats). Un mouvement vertical ne doit
// JAMAIS dériver vers une colonne voisine (sinon gel dans une colonne sans échelle,
// ou rebond hors d'un trou en chute). Même arbitrage que T5 pour l'escalade.
function alignXFloor(r: { x: number }) {
  r.x &= ~7
}
// Recalage x à l'ATTERRISSAGE d'une chute. Par défaut = alignXFloor (colonne du bord GAUCHE, voulu
// pour les trous : cf. hole-approach « plancher = plus proche »). MAIS si le coureur atterrit À CHEVAL
// sur une ÉCHELLE (désaligné, échelle sous une case/dessous d'un bord mais PAS du bord gauche), on le
// recale SUR la colonne de l'échelle au lieu du bord gauche : sinon le snap le renvoie dans le vide
// voisin, il reperd le support et re-tombe -> oscillation « bloqué au bord d'une échelle » (bug 21/07,
// « en chute impossible d'attraper l'échelle »). Aligné (colL === colR) : aucun effet -> inchangé.
function alignXLand(s: GameState, r: { x: number; y: number }) {
  const row = r.y >> 3, colL = r.x >> 3, colR = (r.x + SUB - 1) >> 3
  if (colL !== colR) {
    const onCol = (c: number): boolean => isLadder(tileAt(s, c, row)) || isLadder(tileAt(s, c, row + 1))
    const ladderCol = onCol(colR) ? colR : onCol(colL) ? colL : -1
    if (ladderCol >= 0) { r.x = ladderCol * SUB; return }
  }
  alignXFloor(r)
}
// Glissade sur glace (Task 10). Une entité debout sur de la glace (standsOnIce) est
// FORCÉE de se déplacer dans sa direction `face` : les inputs gauche/droite ne peuvent
// ni l'arrêter ni l'inverser. La glissade se termine au mur (case cible solide, au
// centre) — les inputs redeviennent alors effectifs — ou quand la case dessous n'est
// plus de la glace. La priorité verticale (climb) s'applique AVANT cette fonction :
// monter/descendre une échelle aligné reste permis, et le creusage court avant tout.
function forcedIceDir(s: GameState, r: Runner): -1 | 1 | null {
  if (!standsOnIce(s, r)) return null
  const targetCol = (r.x >> 3) + r.face
  if (r.x % SUB === 0 && isSolid(tileAt(s, targetCol, r.y >> 3))) return null // mur : glissade finie
  return r.face
}
// --------------------------------------------------------------- ennemis AQUATIQUES (16/07 soir)
// Ils ne vivent QUE dans l'eau (Water/Bubbles) : leurs déplacements testent la tuile d'arrivée
// au CENTRE (x/y % 8 === 0), comme la marche — jamais un sous-pas hors de l'eau. Déterministe
// pur (aucun RNG) : patrouilles et oscillations ne dépendent que du tick et du terrain.
const PIRANHA_SIGHT = 8 // portée de détection d'un nageur (cases d'eau CONTINUES, même rangée)
/** Immergé : eau ou source de bulles. EXPORTÉ (06/08) — le joystick tactile en a besoin pour
 *  rendre la diagonale au nageur, et cette question n'a droit qu'à UNE définition. */
export const isWaterT = (t: T) => t === T.Water || t === T.Bubbles

/**
 * Cette entité NAGE-T-ELLE en ce moment ? (sa case-centre est de l'eau ou une source de bulles.)
 *
 * ⚠️ NE PAS DÉDUIRE L'IMMERSION DU SOUFFLE. La tentation est grande — `breath < BREATH_TICKS`
 * tient en trois mots et ne demande pas l'état — mais c'est FAUX sur les sources de bulles : elles
 * RECHARGENT le souffle jusqu'au plein, donc un nageur posé dessus s'y lit « au sec » alors qu'il
 * est sous l'eau. C'est exactement le décor de « De bulle en bulle ». Pour un habillage, l'erreur
 * se voit à peine ; pour les COMMANDES, elle couperait la diagonale en plein bassin.
 */
export function estImmerge(s: GameState, e: { x: number; y: number }): boolean {
  return isWaterT(tileAt(s, e.x >> 3, e.y >> 3))
}

function stepFishes(s: GameState) {
  for (const f of s.fishes) {
    if (f.respawnAt !== 0) { // soufflé par une bombe : réapparition chez lui, comme un moine
      if (s.tick >= f.respawnAt) {
        const { x, y } = centerOf(f.homeCell, s.cols)
        f.x = x; f.y = y; f.respawnAt = 0; f.stunUntil = 0
      }
      continue
    }
    if (f.stunUntil < s.tick) { // un poisson tazé flotte sur place (comme un moine gelé)
      if (f.kind === 'piranha') stepPiranha(s, f)
      else stepMeduse(s, f)
    }
    // Contact (case-centre partagée) : le piranha CROQUE, la méduse PARALYSE. Le bouclier de
    // réanimation et le dash protègent des deux (mêmes gardes que le contact moine).
    for (const r of s.runners) {
      if (!r.alive || r.invulnUntil > s.tick || r.dashUntil > s.tick) continue
      if (cellAt(f.x, f.y, s.cols) !== cellAt(r.x, r.y, s.cols)) continue
      if (f.kind === 'piranha') killRunner(s, r, 'fish')
      else if (r.stunUntil < s.tick) { // re-piqûre seulement une fois la paralysie finie
        r.stunUntil = s.tick + MEDUSE_STUN_TICKS
        s.events.push({ type: 'stung', runner: s.runners.indexOf(r) })
      }
    }
  }
}

// Piranha : patrouille horizontale (1 su / 2 ticks), demi-tour au bord de son eau. S'il voit un
// coureur IMMERGÉ sur SA rangée en eau continue (<= PIRANHA_SIGHT cases), il CHARGE à 2 su/tick —
// plus vite que la nage (1 su/tick) : sur sa rangée, on ne le sème pas, on l'esquive.
function stepPiranha(s: GameState, f: Fish) {
  const row = f.y >> 3
  let target = 0 // dx en cases vers la proie la plus proche (0 = aucune)
  for (const r of s.runners) {
    if (!r.alive || (r.y >> 3) !== row) continue
    if (!isWaterT(tileAt(s, r.x >> 3, row))) continue // proie = nageur (au sec : invisible pour lui)
    const dc = (r.x >> 3) - (f.x >> 3)
    if (dc === 0 || Math.abs(dc) > PIRANHA_SIGHT) continue
    const step = dc > 0 ? 1 : -1
    let clear = true
    for (let c = (f.x >> 3) + step; c !== (r.x >> 3); c += step) {
      if (!isWaterT(tileAt(s, c, row))) { clear = false; break }
    }
    if (clear && (target === 0 || Math.abs(dc) < Math.abs(target))) target = dc
  }
  if (target !== 0) f.face = target > 0 ? 1 : -1
  else if (s.tick % 2 !== 0) return // pas de proie : patrouille tranquille à mi-vitesse
  const steps = target !== 0 ? 2 : 1
  for (let i = 0; i < steps; i++) {
    if (f.x % SUB === 0) {
      const ahead = tileAt(s, (f.x >> 3) + f.face, row)
      if (!isWaterT(ahead)) {
        if (target === 0) f.face = f.face === 1 ? -1 : 1 // patrouille : demi-tour (repart au tick suivant)
        return // charge : bord d'eau = stop net (il ne saute JAMAIS hors de l'eau)
      }
    }
    f.x += f.face
  }
}

// Méduse : oscillation verticale lente (1 su / 3 ticks), bornée par son eau. Prévisible et
// hypnotique : un obstacle mobile, pas un chasseur.
function stepMeduse(s: GameState, f: Fish) {
  if (s.tick % 3 !== 0) return
  if (f.y % SUB === 0) {
    const ahead = tileAt(s, f.x >> 3, (f.y >> 3) + f.vdir)
    if (!isWaterT(ahead)) { f.vdir = f.vdir === 1 ? -1 : 1; return }
  }
  f.y += f.vdir
}

// ------------------------------------------------ INTERRUPTEURS & BARRIÈRES (17/07)
// Circuit GLOBAL : un bouton (T.Button) occupé par un coureur vivant, un moine actif ou une
// caisse = circuit PRESSÉ -> toutes les barrières s'OUVRENT (Barrier -> BarrierOpen, mutation
// du terrain : hashée/sérialisée naturellement, et isSolid suit tout seul — BFS moine compris).
// Relâché -> elles se REFERMENT : un coureur resté dedans est APLATI (sauf dash/bouclier), un
// moine tué, une caisse DÉTRUITE (même le métal : c'est une presse). Événement à la bascule.
// Relâchement TEMPORISÉ (fix 18/07, retour Claude-Alain « le bouton ne fonctionne pas ») :
// avec la refermeture instantanée, presser puis COURIR vers la barrière la trouvait toujours
// fermée — le circuit était injouable en solo sans caisse. Les barrières restent désormais
// ouvertes 3 s après le relâchement (le temps de traverser ~11 cases) ; une caisse posée sur
// le bouton reste le maintien PERMANENT. Le rendu fait clignoter les barrières sur les 1,5
// dernières secondes (annonce du couperet).
const BUTTON_LINGER_TICKS = 90
function stepButtons(s: GameState): void {
  let sawButton = false, pressed = false
  for (let cell = 0; cell < s.terrain.length; cell++) {
    if (s.terrain[cell] !== T.Button) continue
    sawButton = true
    const col = colOf(cell, s.cols), row = rowOf(cell, s.cols)
    if (s.runners.some((r) => r.alive && (r.x >> 3) === col && (r.y >> 3) === row) ||
      s.monks.some((m) => m.respawnAt === 0 && (m.x >> 3) === col && (m.y >> 3) === row) ||
      crateAt(s, col, row) !== null) { pressed = true; break }
  }
  if (!sawButton) return // aucune carte à circuit : zéro travail, zéro événement
  if (pressed) s.buttonLinger = s.tick + BUTTON_LINGER_TICKS
  const held = pressed || s.tick < s.buttonLinger // pressé MAINTENANT, ou dans la grâce de relâchement
  let flipped = 0
  for (let cell = 0; cell < s.terrain.length; cell++) {
    const t = s.terrain[cell]
    if (held && t === T.Barrier) { s.terrain[cell] = T.BarrierOpen; flipped++ }
    else if (!held && t === T.BarrierOpen) {
      s.terrain[cell] = T.Barrier; flipped++
      const col = colOf(cell, s.cols), row = rowOf(cell, s.cols)
      for (const r of s.runners) {
        if (r.alive && r.dashUntil <= s.tick && r.invulnUntil <= s.tick && (r.x >> 3) === col && (r.y >> 3) === row) killRunner(s, r, 'crushed')
      }
      for (const m of s.monks) {
        if (m.respawnAt === 0 && (m.x >> 3) === col && (m.y >> 3) === row) killMonk(s, m)
      }
      const c = crateAt(s, col, row)
      if (c) breakCrate(s, c) // la presse ne fait pas le tri : bois, rocher ET métal
    }
  }
  if (flipped > 0) s.events.push({ type: 'barrier', open: held })
}

// Collision moine/coureur (Task 8) : si la case-centre d'un moine vivant NON piégé
// coïncide avec celle d'un coureur vivant, le coureur meurt. Un moine piégé est
// inoffensif (on lui marche dessus).
function checkCollisions(s: GameState) {
  for (const m of s.monks) {
    if (m.respawnAt !== 0 || m.trappedCell >= 0 || m.stunUntil >= s.tick || m.dormant) continue // STATUE (gong) : inoffensive tant qu'elle dort
    if (m.momieUntil > s.tick) continue // ENROULÉ (06/08) : un moine en cocon ne tue personne
    for (const r of s.runners) {
      if (!r.alive) continue
      if (r.invulnUntil > s.tick) continue // bouclier de réanimation (16/07 soir) : le moine campeur ne re-tue pas
      if (r.deguiseUntil > s.tick) continue // FAUX MOINE (05/08) : les vrais ne le tuent pas (ne l'aident pas non plus)
      const mc = cellAt(m.x, m.y, s.cols), rc = cellAt(r.x, r.y, s.cols)
      // GÉANT (05/08) : deux cases de haut (pieds + tête), immunisé au contact — c'est LUI qui
      // APLATIT (mort standard du moine : killMonk, objets lâchés, boss = 1 coup encaissé et
      // réapparition différée). Les piégés/dormants/emmurés sont déjà exclus plus haut : on
      // n'aplatit pas la pierre ni les captifs. `break` : le moine est parti en réapparition,
      // un second géant qui le chevaucherait le même tick ne doit pas ré-aplatir un mort.
      if (r.geantUntil > s.tick) {
        if (mc === rc || mc === rc - s.cols) { killMonk(s, m); s.events.push({ type: 'ecrase', cell: mc }); break }
        continue
      }
      if (!(r.dashUntil > s.tick) && mc === rc) killRunner(s, r, 'monk') // dash (Lot D) = invulnerable (meme test que checkLava, sur-du si dashUntil absent)
    }
  }
}
// --- Événements aléatoires (mode survie, VERSUS + eventsEnabled) -------------------------
// À intervalle fixe, un événement dangereux surgit, tiré au hasard SEEDÉ (rngInt sur s.rngState) :
// les deux clients et le rejeu voient le même, au même tick. Réutilise les systèmes existants
// (moines / bombes / trous) -> l'effet est déjà rendu par les snapshots (aucun rendu nouveau).
// TOUS les tirages RNG des événements sont ICI, sous eventsEnabled : off = ZÉRO tirage = partie
// strictement identique à avant (la séquence RNG des moines n'est pas perturbée).
const EVENT_INTERVAL = 400 // ~13 s à 30 Hz

function stepEvents(s: GameState): void {
  if (s.mode !== 'versus' || !s.eventsEnabled) return
  if (s.tick === 0 || s.tick % EVENT_INTERVAL !== 0) return
  const kind = rngInt(s, 3)
  if (kind === 0) { eventMonks(s); s.events.push({ type: 'hazard', kind: 'monks' }) }
  else if (kind === 1) { eventBombs(s); s.events.push({ type: 'hazard', kind: 'bombs' }) }
  else { eventCollapse(s); s.events.push({ type: 'hazard', kind: 'collapse' }) }
}

// Zone d'exclusion des apparitions (Tâche 3.6) : un danger (moine/bombe/éboulement) ne doit JAMAIS
// surgir à <= 2 cases (Chebyshev : |dx|<=2 ET |dy|<=2) d'un runner VIVANT (r.alive) — sinon c'est
// une mort sans recours. Purement géométrique : AUCUN tirage RNG ici. Les 3 générateurs bâtissent
// leur LISTE de cases valides (hors de cette zone), puis tirent UN SEUL index dedans
// (valid[rngInt(s, valid.length)]) : nombre de tirages CONSTANT, jamais de « re-tire tant que trop
// près » (qui, à nombre de tirages variable, décalerait la séquence RNG et casserait rejeu + multi).
// rngInt(s, 0) consomme QUAND MÊME 1 tirage et renvoie 0 (cf. rng.ts) : sur liste vide (cas extrême
// « joueurs partout ») -> index 0 -> valid[0] === undefined -> pas d'apparition ce cycle, MAIS le
// tirage a bien eu lieu (compte de tirages inchangé -> rejeu/multi intacts). r.alive = coureur sur le
// plateau (un éliminé ou un mort en attente de réapparition n'y est pas, donc pas protégé : correct).
const EVENT_SAFE_RADIUS = 2
function nearLivingRunner(s: GameState, col: number, row: number): boolean {
  for (const r of s.runners) {
    if (!r.alive) continue
    if (Math.abs(col - (r.x >> 3)) <= EVENT_SAFE_RADIUS && Math.abs(row - (r.y >> 3)) <= EVENT_SAFE_RADIUS) return true
  }
  return false
}

// Vague de moines : 1 ou 2 moines apparaissent à la 1re case non solide (de haut en bas) d'une
// colonne VALIDE (hors zone d'exclusion) ; ils tombent puis traquent (stepMonks au tick suivant).
// Tirages INCHANGÉS vs l'origine : 1 pour n, puis 1 index de case valide par moine (avant : 1 colonne).
function eventMonks(s: GameState): void {
  const MAX_ACTIVE = 8 // plafond runtime : pas d'invasion de moines en partie longue (finding revue)
  const n = 1 + rngInt(s, 2)
  for (let i = 0; i < n; i++) {
    if (s.monks.filter((m) => m.respawnAt === 0).length >= MAX_ACTIVE) break
    const valid: number[] = [] // cases d'apparition (1re non-solide de la colonne) hors exclusion
    for (let col = 0; col < s.cols; col++) {
      let row = 0
      while (row < s.rows && isSolid(tileAt(s, col, row))) row++
      if (row < s.rows && !nearLivingRunner(s, col, row)) valid.push(cellIndex(col, row, s.cols))
    }
    const cell = valid[rngInt(s, valid.length)] // 1 tirage (constant) ; liste vide -> undefined
    if (cell !== undefined) s.monks.push(newMonk(cell, 'normal', s.cols))
  }
}

// Pluie de bombes : 2-3 bombes amorcées à des cases VALIDES (non solides, libres, hors exclusion ;
// row < rows-1 comme avant) au hasard (explosent après FUSE). Tirages : 1 pour n, puis 1 SEUL index
// de case valide par bombe. Avant : col ET row = 2 tirages/bombe -> maintenant 1 index dans la liste
// de CASES valides = 1 tirage/bombe, compte FIXE (jamais variable) — le déterminisme est préservé.
function eventBombs(s: GameState): void {
  const n = 2 + rngInt(s, 2)
  for (let i = 0; i < n; i++) {
    const valid: number[] = []
    for (let row = 0; row < s.rows - 1; row++) for (let col = 0; col < s.cols; col++) {
      const cell = cellIndex(col, row, s.cols)
      if (!isSolid(tileAt(s, col, row)) && !s.bombs.some((b) => b.cell === cell) && !nearLivingRunner(s, col, row)) valid.push(cell)
    }
    const cell = valid[rngInt(s, valid.length)] // 1 tirage (constant) ; liste vide -> undefined
    if (cell !== undefined) {
      s.bombs.push({ cell, explodeAt: s.tick + FUSE_TICKS })
      s.events.push({ type: 'bombPlaced', cell })
    }
  }
}

// Éboulement : 2-3 trous s'ouvrent à des BRIQUES VALIDES (hors exclusion) au hasard (digAt = trou
// repoussable ; qui est au-dessus tombe/est piégé). Uniquement des briques (sinon digAt ferait
// repousser une brique fantôme sur une case vide au moment de la repousse). Tirages : 1 pour n, puis
// 1 SEUL index de brique valide par trou. Avant : col ET row = 2 tirages/trou -> maintenant 1, FIXE.
function eventCollapse(s: GameState): void {
  const n = 2 + rngInt(s, 2)
  for (let i = 0; i < n; i++) {
    const valid: number[] = []
    for (let row = 0; row < s.rows; row++) for (let col = 0; col < s.cols; col++) {
      if (tileAt(s, col, row) === T.Brick && !nearLivingRunner(s, col, row)) valid.push(cellIndex(col, row, s.cols))
    }
    const cell = valid[rngInt(s, valid.length)] // 1 tirage (constant) ; liste vide -> undefined
    if (cell !== undefined) digAt(s, cell)
  }
}

// Fins de partie (Task 7 solo ; lot 4b versus/coop). allHeld = plus aucune pièce au sol
// ni portée par un moine : tout le pot est entre les mains des coureurs (ou détruit).
function checkWin(s: GameState) {
  // AUDIT 16/07 : une caisse-or FERMÉE compte comme de l'or restant. Sans ce test, un niveau dont
  // tout l'or dort dans des caisses révélait sa sortie au PREMIER tick (golds vide) : on le finissait
  // sans jamais casser une caisse. Une caisse détruite en lave sort du décompte (l'or a brûlé : la
  // règle « tout le pot est aux mains des coureurs OU détruit » vaut ici comme pour l'or au sol).
  // ⚠️ MÊME SOURCE QUE LE BANDEAU (`orRestant`, shared/state.ts). Cette condition était écrite ici
  // en toutes lettres et le bandeau en avait sa propre version, fausse : il oubliait l'or volé par
  // un moine et celui resté en caisse. Une seule définition, sinon elles redivergent.
  const allHeld = orRestant(s) === 0
  if (s.mode === 'versus') {
    if (s.runners.length < 2 || s.finished) return
    const atTop = (r: Runner) => r.alive && r.y === 0 && r.x % SUB === 0
    // Indices des runners à l'or MAXIMAL (ex aequo inclus, ordre croissant). Aucun RNG ici :
    // le départage seedé se fait au SEUL point d'appel final via tieBreakWinner (Tâche 3.4).
    // Le seul appelant est la double-élimination ci-dessous (résolution finale) — pas d'usage
    // d'affichage courant, donc aucune version « index le plus bas » à préserver.
    const goldLeaders = (): number[] => {
      let max = -Infinity
      for (const r of s.runners) if (r.gold > max) max = r.gold
      const out: number[] = []
      for (let i = 0; i < s.runners.length; i++) if (s.runners[i]!.gold === max) out.push(i)
      return out
    }
    // Survie : on gagne en restant le DERNIER en vie. Un seul (ou zéro) « en vie » -> fin.
    if (s.lives > 0) {
      const alive = s.runners.filter(r => r.lives > 0)
      if (alive.length <= 1) {
        s.finished = true; s.won = true
        // Double-élimination (0 en vie) : le plus riche gagne ; à or égal, départage SEEDÉ
        // (rngInt) — plus de biais « index le plus bas ». Résolution finale -> rng OK ici.
        if (alive.length === 1) s.winner = s.runners.indexOf(alive[0]!)
        else { const gl = goldLeaders(); s.winner = gl.length === 1 ? gl[0]! : tieBreakWinner(s, gl) }
        s.events.push({ type: 'win', winner: s.winner })
        return
      }
      // 2+ encore en vie : la partie peut aussi se conclure à l'or ci-dessous.
    }
    if (!allHeld) return
    // Pool d'éligibles : en survie, seuls les encore-en-vie ; en illimité, tous.
    const pool = s.runners.map((_, i) => i).filter(i => (s.lives > 0 ? s.runners[i]!.lives > 0 : true))
    const maxGold = Math.max(...pool.map(i => s.runners[i]!.gold))
    const leaders = pool.filter(i => s.runners[i]!.gold === maxGold)
    if (leaders.length === 1) {
      s.finished = true; s.won = true; s.winner = leaders[0]!
      s.events.push({ type: 'win', winner: s.winner })
      return
    }
    // Égalité de tête : l'échelle apparaît, le premier des ex aequo au sommet gagne.
    if (!s.exitRevealed) {
      s.exitRevealed = true
      for (const c of s.exitCells) s.terrain[c] = T.ExitLadder
      s.events.push({ type: 'exitRevealed' })
    }
    // Ex aequo de tête présents au sommet CE tick : un seul -> il gagne (aucun rng) ; plusieurs
    // au même tick -> départage SEEDÉ (Tâche 3.4), plus de biais « premier index ». Tant que
    // personne n'est au sommet, atTopLeaders est vide -> AUCUN rng consommé en cours de partie.
    const atTopLeaders = leaders.filter(i => atTop(s.runners[i]!))
    if (atTopLeaders.length > 0) {
      s.finished = true; s.won = true
      s.winner = atTopLeaders.length === 1 ? atTopLeaders[0]! : tieBreakWinner(s, atTopLeaders)
      s.events.push({ type: 'win', winner: s.winner })
      return
    }
    return
  }
  if (!s.exitRevealed && allHeld) {
    s.exitRevealed = true
    for (const c of s.exitCells) s.terrain[c] = T.ExitLadder
    s.events.push({ type: 'exitRevealed' })
  }
  if (!s.exitRevealed || s.finished) return
  if (s.mode === 'solo') {
    const r = s.runners[0]!
    if (r.alive && r.y === 0 && r.x % SUB === 0) { s.finished = true; s.won = true; s.events.push({ type: 'win', winner: null }) }
  } else { // coop : TOUS vivants au sommet au même tick
    if (s.runners.length > 0 && s.runners.every(r => r.alive && r.y === 0 && r.x % SUB === 0)) {
      s.finished = true; s.won = true
      s.events.push({ type: 'win', winner: null })
    }
  }
}
